import {
  AfterViewInit, ApplicationRef, ChangeDetectionStrategy, ChangeDetectorRef,
  Component, inject, Input, NgZone, OnDestroy, Renderer2, TemplateRef,
  ViewChild, ViewContainerRef, Output, EventEmitter
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import * as THREE from 'three';
import { FikoOrthoKamera } from './IFikoThreeJS/FikoOrthoKamera';
import { FikoSahne } from './IFikoThreeJS/FikoSahne';
import { FikoKontrolCad } from './IFikoThreeJS/FikoKontrolCad';
import { CSS3DRenderer } from "three/examples/jsm/renderers/CSS3DRenderer.js";
import { CSS2DRenderer } from "three/examples/jsm/renderers/CSS2DRenderer.js";
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Fiko3DHizliMenu } from './IFikoThreeJS/fiko-3d-hizli-menu/fiko-3d-hizli-menu.component';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { UzunBasDirective } from '../../../pipes/filoyt-uzun-bas.pipe';
import { FikoDialogService } from '../fiko-dialog-core/fiko-dialog.service';
import { FikoThreeJsAyarlarComponent } from './fiko-threejs-ayarlar/fiko-threejs-ayarlar.component';
import { FikoYTService } from '@/src/service/fikoyt.service';
import { FikoSecililer } from './IFikoThreeJS/FikoSecililer';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { FikoGlowEdgesHelper } from './IFikoThreeJS/FikoGlowEdgesHelper';
import { setCanvasRenderer } from './IFikoThreeJS/FikoCanvasResolution';

// ULTRA PERFORMANS: Import mevcut optimization sınıfları
import { FikoGeometryWorkerService } from './IFikoThreeJS/FikoGeometryWorkerService';
import { FikoPerformanceMonitorComponent } from './IFikoThreeJS/fiko-performance/fiko-performance-monitor.component';

// Mevcut performans modülleri
import { FikoDynamicResolutionScaler } from './IFikoThreeJS/fiko-performance/FikoDynamicResolutionScaler';
import { FikoMeshCompressor } from './IFikoThreeJS/fiko-performance/FikoMeshCompressor';
import { FikoInstancedOptimizer } from './IFikoThreeJS/fiko-performance/FikoInstancedOptimizer';
import { FikoVirtualTextures } from './IFikoThreeJS/fiko-performance/FikoVirtualTextures';

// WebGPU Native Compute
import { FikoWebGPU } from './IFikoThreeJS/fiko-performance/FikoWebGPU';

// 🚀 WebGPU Renderer - Dual mode (WebGPU/WebGL fallback)
import { FikoWebGPURenderer, isWebGPUAvailable, getWebGPUInfo } from './IFikoThreeJS/fiko-performance/FikoWebGPURenderer';

// 🚀 WebGPU Camera Operations - Pan/Rotate/Zoom acceleration
import { FikoWebGPUCameraOps, CameraOpsResult } from './IFikoThreeJS/FikoWebGPUCameraOps';

// 🚀 WebGPU Lighting - Clustered Forward+ & GPU Headlight Updates
import { FikoWebGPULighting, fikoWebGPULighting } from './IFikoThreeJS/FikoWebGPULighting';
import { FikoIsikYonsel } from './IFikoThreeJS/FikoIsikYonsel';

// 🚀🚀🚀 CATIA-LEVEL PERFORMANCE: Unified performance integration (FAZA 4)
import {
  FikoPerformanceIntegration,
  getPerformanceIntegration,
  disposePerformanceIntegration,
  fikoPerformance
} from './IFikoThreeJS/fiko-performance';

// 🎯 GPU Tier Detection & Renderer Factory
import { FikoRendererFactory } from './IFikoThreeJS/fiko-performance/FikoRendererFactory';

// 🎨 CAD Material System - Mat Metal / Plastik görünüm
import { FikoSharedCADMaterials, FikoCADMaterialPreset } from './IFikoThreeJS/FikoCADMaterial';

// 3D Line Dialog
import { FikoYT3dLineDialogComponent, FikoYT3dLine } from './IFikoThreeJS/fiko-3d-elements/fikoyt-3d-line';

// Dosya Import (sürükle-bırak için)
import { FikoImporter, ALL_SUPPORTED_FORMATS } from './IFikoThreeJS/FikoImporter';

// Real-Time Collaboration
import { FikoCollabDialogComponent } from './IFikoThreeJS/fiko-collab';
import { FikoCollabService } from './services/fiko-collab.service';

// BufferGeometryUtils için opsiyonel import
// import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

declare var TWEEN: any;

// Performans İzleme Sınıfı
class PerformanceMonitor {
  private frameCount = 0;
  private lastTime = performance.now();
  private fps = 60;
  private frameTimeSum = 0;
  private frameTimeSamples = 0;
  
  update(): { fps: number; frameTime: number } {
    this.frameCount++;
    const currentTime = performance.now();
    const deltaTime = currentTime - this.lastTime;
    
    this.frameTimeSum += deltaTime;
    this.frameTimeSamples++;
    
    if (deltaTime >= 1000) {
      this.fps = Math.round((this.frameCount * 1000) / deltaTime);
      this.frameCount = 0;
      this.lastTime = currentTime;
    }
    
    const avgFrameTime = this.frameTimeSamples > 0 
      ? this.frameTimeSum / this.frameTimeSamples 
      : 0;
    
    if (this.frameTimeSamples > 60) {
      this.frameTimeSum = 0;
      this.frameTimeSamples = 0;
    }
    
    return { fps: this.fps, frameTime: avgFrameTime };
  }
}

// Frustum Culling Optimizasyonu - PERFORMANS OPTIMIZED
class FrustumCuller {
  private frustum = new THREE.Frustum();
  private projScreenMatrix = new THREE.Matrix4();

  // PERFORMANS: Reuse Box3 instance instead of creating new one every time
  private tempBox3 = new THREE.Box3();

  // PERFORMANS: Cache bounding boxes for static objects
  private boundingBoxCache = new WeakMap<THREE.Object3D, THREE.Box3>();

  updateFrustum(camera: THREE.Camera): void {
    this.projScreenMatrix.multiplyMatrices(
      camera.projectionMatrix,
      camera.matrixWorldInverse
    );
    this.frustum.setFromProjectionMatrix(this.projScreenMatrix);
  }

  isVisible(object: THREE.Object3D): boolean {
    if (!(object as any).geometry) return true;

    // PERFORMANS: Try to use cached bounding box
    let box = this.boundingBoxCache.get(object);
    if (!box) {
      // Create and cache the bounding box
      this.tempBox3.setFromObject(object);
      box = this.tempBox3.clone();
      this.boundingBoxCache.set(object, box);
    }

    return this.frustum.intersectsBox(box);
  }

  // PERFORMANS: Invalidate cache when object transforms change
  invalidateCache(object: THREE.Object3D): void {
    this.boundingBoxCache.delete(object);
  }

  // PERFORMANS: Clear all cached boxes on complete rebuild
  clearCache(): void {
    // WeakMap clears automatically when objects are garbage collected
    // But we can help by creating a new instance
    this.boundingBoxCache = new WeakMap();
  }
}

// LOD (Level of Detail) Yöneticisi
class LODManager {
  private lodLevels: Map<string, THREE.LOD> = new Map();
  
  createLOD(id: string, meshes: THREE.Mesh[], distances: number[]): THREE.LOD {
    const lod = new THREE.LOD();
    meshes.forEach((mesh, i) => {
      lod.addLevel(mesh, distances[i] || i * 100);
    });
    this.lodLevels.set(id, lod);
    return lod;
  }
  
  updateLODs(camera: THREE.Camera): void {
    this.lodLevels.forEach(lod => lod.update(camera));
  }
  
  dispose(): void {
    this.lodLevels.forEach(lod => {
      lod.traverse(child => {
        const mesh = child as THREE.Mesh;
        mesh.geometry?.dispose();
        if (mesh.material) {
          const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
          materials.forEach(m => m.dispose());
        }
      });
      lod.parent?.remove(lod);
    });
    this.lodLevels.clear();
  }
}
 
// Object Pooling Sistemi
class ObjectPool<T> {
  private available: T[] = [];
  private inUse = new Set<T>();
  
  constructor(private factory: () => T, initialSize = 10) {
    for (let i = 0; i < initialSize; i++) {
      this.available.push(factory());
    }
  }
  
  acquire(): T {
    let obj = this.available.pop();
    if (!obj) obj = this.factory();
    this.inUse.add(obj);
    return obj;
  }
  
  release(obj: T): void {
    if (this.inUse.delete(obj)) {
      this.available.push(obj);
    }
  }
  
  clear(): void {
    this.available = [];
    this.inUse.clear();
  }
}

// Sahne Optimizasyon Yöneticisi
class SceneOptimizer {
  private instancedMeshes = new Map<string, THREE.InstancedMesh>();
  private mergedGeometries = new Map<string, THREE.BufferGeometry>();
  
  createInstancedMesh(
    geometry: THREE.BufferGeometry, 
    material: THREE.Material, 
    count: number,
    id: string
  ): THREE.InstancedMesh {
    const mesh = new THREE.InstancedMesh(geometry, material, count);
    this.instancedMeshes.set(id, mesh);
    return mesh;
  }
  
  mergeGeometries(geometries: THREE.BufferGeometry[], id: string): THREE.BufferGeometry {
    // Manuel geometry merge (BufferGeometryUtils kullanmadan)
    if (geometries.length === 0) return new THREE.BufferGeometry();
    if (geometries.length === 1) return geometries[0];
    
    // İlk geometry'yi temel al
    const merged = geometries[0].clone();
    
    // Diğer geometry'leri ekle (basitleştirilmiş versiyon)
    // Not: Tam bir merge için BufferGeometryUtils gerekir
    // Bu sadece placeholder, production'da three/examples/jsm/utils/BufferGeometryUtils kullanın
    
    this.mergedGeometries.set(id, merged);
    return merged;
  }
  
  dispose(): void {
    this.instancedMeshes.forEach(mesh => {
      mesh.geometry.dispose();
      if (Array.isArray(mesh.material)) {
        mesh.material.forEach(m => m.dispose());
      } else {
        mesh.material.dispose();
      }
    });
    this.mergedGeometries.forEach(geo => geo.dispose());
    this.instancedMeshes.clear();
    this.mergedGeometries.clear();
  }
}

// Post-processing Efektleri
class PostProcessingManager {
  private composer: any; // EffectComposer
  private passes: Map<string, any> = new Map();
  
  initialize(renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.Camera): void {
    // Burada EffectComposer ve Pass'ler eklenebilir
    // Bloom, SSAO, DOF, vb.
  }
  
  addPass(name: string, pass: any): void {
    this.passes.set(name, pass);
  }
  
  removePass(name: string): void {
    this.passes.delete(name);
  }
  
  render(): void {
    if (this.composer) {
      this.composer.render();
    }
  }
  
  dispose(): void {
    this.passes.clear();
  }
}

@Component({
  selector: 'fikoyt-fiko-three-js',
  standalone: true,
  templateUrl: './fiko-three-js.component.html',
  styleUrl: './fiko-three-js.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, Fiko3DHizliMenu, UzunBasDirective]
})
export class FikoThreeJsComponent implements OnDestroy, AfterViewInit {
  // 🐛 Debug Modu - Performans için static ve koşullu
  public static DEBUG_MODE = false;

  // 🚀 PERFORMANS: Pre-allocated Vector3 defaults for light operations (zero allocation)
  private static readonly _defaultLightOffset = new THREE.Vector3();
  private static readonly _defaultLightTarget = new THREE.Vector3();

  // 🐛 Performanslı debug log helper (sadece DEBUG_MODE açıksa çalışır)
  public static debugLog(mesaj: string, ...args: any[]): void {
    if (FikoThreeJsComponent.DEBUG_MODE) {
      console.log(`[FikoThreeJS Debug] ${mesaj}`, ...args);
    }
  }

  @ViewChild('canvas3d', { static: true }) canvas: any;
  @ViewChild('canvasCSS3d', { static: true }) canvasCSS3D: any;
  @ViewChild('Dialog3DHizliMenu') Dialog3DHizliMenu: Fiko3DHizliMenu;
  @ViewChild('itemMenu', { static: false }) itemMenu: TemplateRef<any>;

  // Input/Output
  @Input() antiAlias = true;
  @Input() enableShadows = false;
  @Input() enablePostProcessing = false;
  // ⚡ TUNED: maxFPS reduced from 90 to 60 for optimal GPU utilization and power efficiency
  @Input() maxFPS = 60;
  @Input() pixelRatio = window.devicePixelRatio;
  @Input() autoResize = true;
  // ⚡ TUNED: Enable frustum culling by default for performance
  @Input() enableFrustumCulling = true;
  // ⚡ TUNED: Enable LOD system by default for performance
  @Input() enableLOD = true;

  /** 🪶 Gömülü mod (kart önizlemesi): sağ-tık menüsü ve uzun-bas hızlı menüsü tamamen devre dışı — sadece orbit/zoom */
  @Input() gomulu = false;
  
  @Output() performanceUpdate = new EventEmitter<{ fps: number; frameTime: number }>();
  @Output() sceneReady = new EventEmitter<void>();
  @Output() renderComplete = new EventEmitter<void>();

  // Smart Point düzenleme eventi - sağ tıklama ile tetiklenir
  public smartPointEditRequest = new EventEmitter<any>();
  // Düzenlenecek Smart Point - dialog açıldığında kontrol edilir
  public pendingSmartPointEdit: any = null;

  // Render & State
  public renderListeners: Map<string, any> = new Map();
  overlayRef: OverlayRef | null;
  mesgul = false;
  animationFrameId: any;
  private lastFrameTime = 0;
  private frameInterval: number;
  private frameCount: number = 0;

  // GPU bellek izleme
  private _memoryCheckCounter = 0;
  private _lastGeometryCount = 0;
  private _lastTextureCount = 0;
  private _lastLeakWarnTime = 0;

  /** GPU bellek ve render istatistikleri - dış bileşenler okuyabilir */
  public gpuBilgi: {
    geometries: number;
    textures: number;
    drawCalls: number;
    triangles: number;
    points: number;
    lines: number;
  } = { geometries: 0, textures: 0, drawCalls: 0, triangles: 0, points: 0, lines: 0 };

  /** Yükleme durumu - progress ve mesaj için */
  public yuklemeDurumu = {
    aktif: false,
    mesaj: '',
    progress: 0
  };

  /** Sürükle-bırak drop zone aktiflik durumu */
  public dropZoneAktif = false;
  private _dropZoneCounter = 0;

  // Scene & Camera
  private _sahneler: FikoSahne[] = [];
  public get sahneler(): FikoSahne[] { return this._sahneler; }
  public set sahneler(v: FikoSahne[]) { this._sahneler = v; }
  
  aktifKamera?: FikoOrthoKamera;
  aktifKontrol?: any;
  private _arModu: any = null;
  helper?;
  private _aktifItem: any;
  public get aktifItem(): any { return this._aktifItem; }
  public set aktifItem(v: any) { this._aktifItem = v; }

  // Render mode durumu (template'ten çağrılıyor)
  public get renderModeDurumu(): string {
    if (this.renderMode === 'continuous') return '🔴 Sürekli';
    return '🟢 İsteğe Bağlı';
  }

  // Renderers
  public sunucu: THREE.WebGLRenderer;
  public sunucuCSS3d: CSS3DRenderer;
  public sunucuCSS2d: CSS2DRenderer;
  private _css3dStyleEl: HTMLStyleElement | null = null;
  private _onContextLost: ((e: Event) => void) | null = null;
  private _onContextRestored: (() => void) | null = null;
  public css3dSahne: THREE.Scene;

  private nzNotification = inject(NzNotificationService);

  // 🚀 WebGPU Renderer (opsiyonel - seçimli)
  private webGPURenderer: FikoWebGPURenderer | null = null;
  private _useWebGPU: boolean = false;  // WebGPU kullanılsın mı?
  public webGPUAvailable: boolean = false;  // WebGPU destekleniyor mu?

  // Selection & Materials
  public secililer: FikoSecililer;

  // WebGPU Thickness Analysis
  public gpuThickness: any = null;
  public webGPU: FikoWebGPU | null = null;
  private thicknessAnalysisActive: boolean = false;

  // 🚀 LAZY-INIT MATERIALS: GPU memory only allocated on first use
  private static _standartMaterialMesh: THREE.MeshPhongMaterial | null = null;
  public static get standartMaterialMesh(): THREE.MeshPhongMaterial {
    if (!FikoThreeJsComponent._standartMaterialMesh) {
      FikoThreeJsComponent._standartMaterialMesh = new THREE.MeshPhongMaterial({
        vertexColors: true,
        premultipliedAlpha: true,
        dithering: true,
        side: THREE.DoubleSide,
        shadowSide: THREE.DoubleSide,
        polygonOffset: true,
        polygonOffsetFactor: 1,
        polygonOffsetUnits: 1,
        transparent: true
      });
    }
    return FikoThreeJsComponent._standartMaterialMesh;
  }
  public get standartMaterialMesh() { return FikoThreeJsComponent.standartMaterialMesh; }

  private static _standartMaterialCizgiEdge: THREE.LineBasicMaterial | null = null;
  public static get standartMaterialCizgiEdge(): THREE.LineBasicMaterial {
    if (!FikoThreeJsComponent._standartMaterialCizgiEdge) {
      FikoThreeJsComponent._standartMaterialCizgiEdge = new THREE.LineBasicMaterial({
        linewidth: 2,
        polygonOffset: true,
        polygonOffsetUnits: 1,
        polygonOffsetFactor: 1,
        side: THREE.DoubleSide,
        vertexColors: true,
        premultipliedAlpha: true,
        transparent: true,
        alphaTest: 0.01
      });
    }
    return FikoThreeJsComponent._standartMaterialCizgiEdge;
  }
  public get standartMaterialCizgiEdge() { return FikoThreeJsComponent.standartMaterialCizgiEdge; }

  private static _standartMaterialPointVertex: THREE.PointsMaterial | null = null;
  public static get standartMaterialPointVertex(): THREE.PointsMaterial {
    if (!FikoThreeJsComponent._standartMaterialPointVertex) {
      FikoThreeJsComponent._standartMaterialPointVertex = new THREE.PointsMaterial({
        size: 1,
        color: 0x000000,
        vertexColors: true,
        premultipliedAlpha: true,
        transparent: false
      });
    }
    return FikoThreeJsComponent._standartMaterialPointVertex;
  }
  public get standartMaterialPointVertex() { return FikoThreeJsComponent.standartMaterialPointVertex; }

  // Loaders & Utilities
  bufferGeometryLoader: THREE.BufferGeometryLoader = new THREE.BufferGeometryLoader();
  dc: any;
  private dialogKapandiSubs: any;

  // Real-Time Collaboration
  private collabSelectionSub: any;
  private collabCursorSub: any;
  private collabRaycaster: THREE.Raycaster = new THREE.Raycaster();
  private collabMouseNDC: THREE.Vector2 = new THREE.Vector2();

  // Performans ve Optimizasyon Sistemleri
  private performanceMonitor = new PerformanceMonitor();
  private frustumCuller = new FrustumCuller();
  private lodManager = new LODManager();
  private sceneOptimizer = new SceneOptimizer();
  private postProcessing = new PostProcessingManager();
  
  // Object Pooling
  private meshPool = new ObjectPool<THREE.Mesh>(() => new THREE.Mesh(), 50);
  private geometryPool = new ObjectPool<THREE.BufferGeometry>(
    () => new THREE.BufferGeometry(), 
    20
  );

  // PERFORMANS: Caches with size limits to prevent VRAM exhaustion
  private textureCache = new Map<string, THREE.Texture>();
  private textureCacheAccessOrder: string[] = []; // Track LRU order
  // ⚡ TUNED: Aggressive texture cache for 60+ FPS on mobile
  private readonly MAX_TEXTURE_CACHE_SIZE = 50; // Reduced from 100 (faster LRU)
  private readonly MAX_TEXTURE_MEMORY_MB = 256; // Reduced from 512 (mobile friendly)

  private materialCache = new Map<string, THREE.Material>();
  // ⚡ TUNED: Material cache optimization
  private readonly MAX_MATERIAL_CACHE_SIZE = 100; // Reduced from 200 (less GC pressure)

  private geometryCache = new Map<string, THREE.BufferGeometry>();
  // ⚡ TUNED: Geometry cache optimization
  private readonly MAX_GEOMETRY_CACHE_SIZE = 75; // Reduced from 150 (faster reuse)

  // PERFORMANS: Track cache memory usage
  private estimatedTextureCacheMemory = 0;

  // ⚡ TUNED: Performance metrics tracking
  private lastGPUMemoryCheck = 0;
  private gpuMemoryCheckInterval = 5000; // Check every 5 seconds instead of every frame

  // ULTRA PERFORMANS: Advanced optimization systems (eksik modüller devre dışı)
  private raycasterBVH: any = null;
  private gpuCompute: any = null;
  private gpuComputeOptimized: any = null;
  private advancedLOD: any = null;
  private streamingLODManager: any = null; // Streaming LOD for large CAD models
  private occlusionCulling: any = null;
  private renderBatchOptimizer: any = null;
  private gpuMemoryManager: any = null;
  private geometryWorkerService: FikoGeometryWorkerService | null = null;

  // TOP 5 ULTRA PERFORMANS: Latest optimization systems
  private dynamicResolutionScaler: FikoDynamicResolutionScaler | null = null;
  private screenSpaceAO: any = null;
  private temporalAA: any = null;
  private gpuParticleCompute: any = null;

  // NEXT 5 ULTRA PERFORMANS: Second wave optimization systems
  private objectPoolManager: any = null;
  private meshCompressor: FikoMeshCompressor | null = null;
  private textureStreaming: any = null;

  // EXTREME 10 MORE ULTRA PERFORMANS: Third wave optimization systems
  private aggressiveLOD: any = null;
  private computeWorkerPool: any = null;
  private zPrepass: any = null;
  private frameBudget: any = null;
  private instancedOptimizer: FikoInstancedOptimizer | null = null;
  private gpuDrivenRenderer: any = null;
  private visibilityPrecompute: any = null;
  private virtualTextures: FikoVirtualTextures | null = null;
  private neuralCompression: any = null;

  // GPU COMPUTE EXTREME 10 MORE OPTIMIZATIONS: Fourth wave (eksik modüller devre dışı)
  private gpuPhysicsCompute: any = null;
  private gpuFrustumCulling: any = null;
  private gpuOcclusionQueries: any = null;
  private gpuTransformFeedback: any = null;
  private gpuSkinning: any = null;
  private gpuParticleAdvection: any = null;
  private gpuTextureFiltering: any = null;
  private gpuTileDeferredRendering: any = null;
  private gpuAsyncComputePipeline: any = null;
  private gpuStreamOutputList: any = null;

  // 🚀 WEBGPU CAMERA OPS: Pan/Rotate/Zoom GPU acceleration
  private webgpuCameraOps: FikoWebGPUCameraOps | null = null;
  private webgpuCameraOpsEnabled = true;
  private cachedObjectBounds: Array<{ center: THREE.Vector3; radius: number; objectIndex: number; object: THREE.Object3D }> = [];
  private objectBoundsCacheValid = false;
  private lastCameraOpsResult: CameraOpsResult | null = null;

  // 🚀 WEBGPU LIGHTING: Clustered Forward+ & GPU Headlight Updates
  private webgpuLighting: FikoWebGPULighting | null = null;
  private webgpuLightingEnabled = true;
  private cachedFikoIsikYonsels: FikoIsikYonsel[] = [];
  private lightCacheValid = false;

  // 🚀🚀🚀 CATIA-LEVEL PERFORMANCE: Unified Performance Integration (FAZA 4)
  private performanceIntegration: FikoPerformanceIntegration | null = null;
  private performanceIntegrationEnabled = true;
  private lastPerformanceUpdateTime = 0;

  // Render Modu
  private renderMode: 'continuous' | 'on-demand' = 'on-demand';
  private needsRender = false;

  // 🚀 PERFORMANS: Interaction state for lightweight rendering during camera movement
  private isInteracting = false;
  private interactionEndTimer: any = null;
  private renderScheduled = false;

  // 🚀 PERFORMANS: Destroyed state - RAF callback'lerinde kontrol için
  private _isDestroyed = false;

  // 🚀 RENDER PERFORMANS: Dynamic quality settings during interaction
  // ⚠️ ÇÖZÜM: 0.9'dan düşük pixel ratio çizgileri bozuyor (kesik kesik görünüm)
  private normalPixelRatio = 1.0;
  private interactionPixelRatio = 0.95; // 95% - Çizgi kalitesi için minimum 0.9 gerekli
  private useInteractionQuality = false; // ⚠️ ŞİMDİLİK KAPALI - Çizgi sorunu nedeniyle

  // 🚀 ULTRA PERFORMANS: Frame timing for adaptive quality
  private lastRenderTime = 0;
  private avgRenderTime = 16.67; // Running average
  private renderTimeSmoothing = 0.9; // EMA smoothing factor

  // 🚀 PERFORMANS: Cache headlight references to avoid scene traversal
  private cachedHeadlights: THREE.Light[] = [];

  // Event handler referansları
  private kameraChangeHandler?: () => void;

  private _onControlStart = () => { this.onCameraInteractionStart(); };
  private _onControlChange = () => { this.onCameraChange(); };
  private _onControlChangeLight = () => { this.markNeedsRender(); };
  private _onControlEnd = () => { this.onCameraInteractionEnd(); };

  // Three.js Ayarları (localStorage'da saklanan)
  private threejsAyarlar: any = {
    renderModu: 'on-demand',
    maxFPS: 60,
    antialiasAktif: true,
    frustumCullingAktif: true,
    lodSistemiAktif: true,
    pixelRatio: 1.0,
    // GPU Optimization
    dynamicResolutionAktif: false,
    gpuParticleComputeAktif: false,
    temporalAAaktif: false,
    textureStreamingAktif: false,
    shadowOptimizerAktif: false,
    // GPU Compute Extreme
    gpuPhysicsAktif: false,
    gpuFrustumCullingAktif: false,
    gpuOcclusionQueriesAktif: false,
    gpuTransformFeedbackAktif: false,
    gpuSkinningAktif: false,
    gpuParticleAdvectionAktif: false,
    gpuTextureFilteringAktif: false,
    gpuTileDeferredRenderingAktif: false,
    gpuAsyncComputeAktif: false,
    gpuStreamOutputAktif: false,
    // Advanced
    aggressiveLODAktif: false,
    computeWorkerPoolAktif: false,
    zPrepassAktif: false,
    frameBudgetAktif: false,
    instancedOptimizerAktif: false,
    gpuDrivenRendererAktif: false,
    visibilityPrecomputeAktif: false,
    aggressiveMergingAktif: false,
    virtualTexturesAktif: false,
    neuralCompressionAktif: false,
    // Seçim ve Highlight - Yüzey
    hoverRengi: '#00ffff',
    hoverOpacity: 0.5,
    secilmisRengi: '#ff0000',
    secilmisOpacity: 0.5,
    // Çizgi
    cizgiHoverRengi: '#00ff00',
    cizgiHoverOpacity: 0.7,
    cizgiSecilmisRengi: '#ffff00',
    cizgiSecilmisOpacity: 0.7,
    cizgiKalinligi: 5,
    // Kenarlar
    kenarGucluklagi: 10,
    kenarYogunluklagi: 1.5,
    kenarKalinligi: 1.0,
    gorunurKenarRengi: '#ffffff',
    gizliKenarRengi: '#000000',
    // Debug
    debugMode: false
  };

  constructor(
    public FikoYT: FikoYTService,
    public readonly CD: ChangeDetectorRef,
    public renderer: Renderer2,
    public sanitizer: DomSanitizer,
    public overlay: Overlay,
    public viewContainerRef: ViewContainerRef,
    public appRef: ApplicationRef,
    private ngZone: NgZone,
    public DialogFiko: FikoDialogService,
    public collabService: FikoCollabService
  ) {
    CD.detach();
    this.frameInterval = 1000 / this.maxFPS;
    
    this.dc = this.FikoYT.degisti.subscribe(() => {
      this.CD.detectChanges();
      this.markNeedsRender();
    });

    this.secililer = new FikoSecililer(this, null, "*", true, true);
  }

  ngAfterViewInit(): void {
    document.body.style.cursor = "url('./cursor/kursor-simge.webp'), auto";
    this.hazirla();
    this.setupEventListeners();

    // Debug: Expose component globally for console access
    (window as any).fikoThreeJS = this;

    // localStorage'den kaydedilen ayarları yükle (baslat'tan önce - hover ayarları için)
    this.loadSavedSettings();

    // Ayarlar yüklendikten sonra secililer'i başlat
    this.secililer.baslat();

    // 🚀 SÜREKLİ ÇİZİM MODU - Ayarlara göre başlat (default: aktif)
    const kaydedilen = localStorage.getItem('FikoThreeJSAyarlari');
    let surekliCizimAktif = true; // Default: aktif
    if (kaydedilen) {
      try {
        const ayarlar = JSON.parse(kaydedilen);
        if (ayarlar.surekliCizimModu !== undefined) {
          surekliCizimAktif = ayarlar.surekliCizimModu;
        }
      } catch (e) { }
    }
    if (surekliCizimAktif) {
      this.surekliCiz();
    }

    // Dialog kapandığında 3D hızlı menü ve context menüyü kapat
    this.dialogKapandiSubs = this.DialogFiko.dialogKapandi$.subscribe(() => {
      // 3D Hızlı menüyü kapat
      this.Dialog3DHizliMenu?.kapa();
      // Context menüyü (sağ tıklama) kapat
      this.secililer?.sagMenuKapat();
    });

    this.sceneReady.emit();

    this.webGPUKontrol();

    // Setup collaboration callbacks
    console.log('[FikoThreeJS] Setting up collab callbacks in ngAfterViewInit');
    this.collabService.onRoomJoinedCallback = () => this.setupCollabEventHandlers();
    this.collabService.onRoomLeftCallback = () => this.cleanupCollabEventHandlers();
  }

  /**
   * localStorage'den kaydedilen Three.js ayarlarını yükle ve uygula
   */
  private loadSavedSettings(): void {
    const kaydedilen = localStorage.getItem('FikoThreeJSAyarlari');
    if (kaydedilen) {
      try {
        let ayarlar = JSON.parse(kaydedilen);

        // Uyumluluk: Eski property isimlerini yenileriyle değiştir
        if ('textureStreamingAktif' in ayarlar) {
          ayarlar.virtualTexturesAktif = ayarlar.textureStreamingAktif;
          delete ayarlar.textureStreamingAktif;
        }

        this.ayarlariUygula(ayarlar);
        FikoThreeJsComponent.debugLog('Kaydedilen ayarlar yuklendi ve uygulandi');
      } catch (e) {
        // Ayarlar yuklenemedi - sessiz gec
      }
    }
  }

  private webGPUKontrol(): void {
    if (navigator.gpu) return;
    const ANAHTAR = 'fikoyt_webgpu_uyari_gosterildi';
    if (localStorage.getItem(ANAHTAR)) return;
    localStorage.setItem(ANAHTAR, '1');
    this.nzNotification.warning(
      'WebGPU Desteklenmiyor',
      'Tarayiciniz WebGPU desteklemiyor. Bazi gelismis ozellikler (sivi simulasyonu, gelismis aydinlatma, GPU hesaplama) kullanilamayacak. WebGL ile devam edilecek.',
      { nzDuration: 8000 }
    );
  }

  private setupEventListeners(): void {
    if (this.autoResize) {
      this.renderListeners.set("resize", 
        this.renderer.listen(window, 'resize', () => this.onWindowResize())
      );
    }

    this.renderListeners.set("pointermove",
      this.renderer.listen(this.canvas.nativeElement, 'pointermove', (event: PointerEvent) => {
        // 🚀🚀🚀 PERFORMANS: Sadece button basılıysa (zoom/pan/rotate) işlem yap
        // Aksi halde FikoSecililer hover işlemlerini halleder
        if (event.buttons === 0) {
          return; // Hover için FikoSecililer kullanılacak
        }

        // 🚀 PERFORMANS: Orta tuş basılı ise (pan) - kontrol güncelle
        if (this.sahneler.length > 0) {
          // 🚀 PERFORMANS: Set interaction mode for lightweight rendering
          this.isInteracting = true;

          // 🚀 PERFORMANS: Update control - render sürekli çizim modunda otomatik
          this.aktifKontrol?.update();

          // 🚀🚀🚀 PERFORMANS: Sürekli çizim modu aktifse RAF çağrısı YAPMA
          // Render loop zaten çalışıyor - gereksiz çift render önlenir
          // Sadece sürekli çizim kapalıysa manuel render gerekli
          if (this.renderMode !== 'continuous' && !this.renderScheduled) {
            this.renderScheduled = true;
            requestAnimationFrame(() => {
              // 🚀 PERFORMANS: Destroyed check - component destroy edilmişse render yapma
              if (this._isDestroyed) return;
              this.sunHizli();
              this.renderScheduled = false;
            });
          }
        }
      })
    );

    // 🚀 PERFORMANS: Detect interaction end for full quality rendering
    this.renderListeners.set("pointerup",
      this.renderer.listen(this.canvas.nativeElement, 'pointerup', () => {
        this.onInteractionEnd();
      })
    );

    this.renderListeners.set("pointerleave",
      this.renderer.listen(this.canvas.nativeElement, 'pointerleave', () => {
        this.onInteractionEnd();
      })
    );

    // Visibility API ile tab aktifliğini kontrol et
    this.renderListeners.set("visibilitychange",
      this.renderer.listen(document, 'visibilitychange', () => {
        if (document.hidden) {
          this.surekliCizIptal();
        } else if (this.renderMode === 'continuous') {
          this.surekliCiz();
        }
      })
    );

    // Sürükle-Bırak: Host element üzerinde dragenter/dragleave/dragover
    const hostEl = this.canvas.nativeElement.parentElement;
    if (hostEl) {
      this.renderListeners.set("dragenter",
        this.renderer.listen(hostEl, 'dragenter', (e: DragEvent) => {
          e.preventDefault();
          this._dropZoneCounter++;
          if (this._dropZoneCounter === 1 && this._hasDosya(e)) {
            this.dropZoneAktif = true;
            this.CD.detectChanges();
          }
        })
      );
      this.renderListeners.set("dragleave",
        this.renderer.listen(hostEl, 'dragleave', (e: DragEvent) => {
          e.preventDefault();
          this._dropZoneCounter--;
          if (this._dropZoneCounter <= 0) {
            this._dropZoneCounter = 0;
            this.dropZoneAktif = false;
            this.CD.detectChanges();
          }
        })
      );
      this.renderListeners.set("dragover_host",
        this.renderer.listen(hostEl, 'dragover', (e: DragEvent) => {
          e.preventDefault();
        })
      );
      this.renderListeners.set("drop_host",
        this.renderer.listen(hostEl, 'drop', (e: DragEvent) => {
          e.preventDefault();
          this._dropZoneCounter = 0;
          this.dropZoneAktif = false;
          this.CD.detectChanges();
          this._dosyaBirak(e);
        })
      );
    }
  }

  // === Sürükle-Bırak Metotları (template binding) ===

  onDragOver(e: DragEvent): void {
    e.preventDefault();
    e.stopPropagation();
  }

  onDragLeave(e: DragEvent): void {
    e.preventDefault();
    e.stopPropagation();
    this._dropZoneCounter = 0;
    this.dropZoneAktif = false;
    this.CD.detectChanges();
  }

  onDrop(e: DragEvent): void {
    e.preventDefault();
    e.stopPropagation();
    this._dropZoneCounter = 0;
    this.dropZoneAktif = false;
    this.CD.detectChanges();
    this._dosyaBirak(e);
  }

  /** DataTransfer'da dosya var mı kontrol et */
  private _hasDosya(e: DragEvent): boolean {
    if (!e.dataTransfer) return false;
    if (e.dataTransfer.types.indexOf('Files') !== -1) return true;
    return false;
  }

  /** Bırakılan dosyaları import et */
  private async _dosyaBirak(e: DragEvent): Promise<void> {
    const files = e.dataTransfer?.files;
    if (!files || files.length === 0) return;
    if (this.sahneler.length === 0) return;

    const dosyalar: File[] = Array.from(files);
    const sahne = this.sahneler[0] as any;
    const cizimler = sahne.cizimler;
    if (!cizimler) return;

    // CATIA gibi: TEK STEP dosyası sürükle-bırakta GERÇEK B-Rep (parametrik, kalıcı) yoluna gider.
    // Çoklu STEP (montaj) ya da diğer formatlar mevcut mesh importer'a düşer.
    const stp = dosyalar.filter(f => /\.(stp|step)$/i.test(f.name));
    if (dosyalar.length === 1 && stp.length === 1) {
      const modelleme = sahne.anaGosterim?.modellemeAl?.();
      if (modelleme?.disStepAl) {
        try {
          const bytes = new Uint8Array(await stp[0].arrayBuffer());
          if (await modelleme.disStepAl(bytes)) {
            this.FikoYT?.degisti?.emit();
            this.ciz();
            setTimeout(() => this.secililer?.registerEdgesForSpatialIndex(), 200);
            return;
          }
        } catch (err) { console.warn('[Drop] STEP B-Rep import hatası, mesh yoluna düşülüyor', err); }
      }
    }

    const importer = new FikoImporter(cizimler);
    const sonuc = await importer.dosyalardanYukle(dosyalar);

    if (sonuc) {
      this.FikoYT?.degisti?.emit();
      this.ciz();
      setTimeout(() => {
        this.secililer?.registerEdgesForSpatialIndex();
      }, 200);
    }
  }

  onWindowResize(): void {
    if (this.sahneler.length === 0) return;

    // 🚀 PERFORMANS: Single getBoundingClientRect call (avoid layout thrashing)
    const rect = this.canvas.nativeElement.parentNode.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;

    this.aktifKamera.left = width / -2;
    this.aktifKamera.right = width / 2;
    this.aktifKamera.top = height / 2;
    this.aktifKamera.bottom = height / -2;

    this.sunucu.setSize(width, height, true);
    this.sunucuCSS3d.setSize(width, height);
    if (this.sunucuCSS2d) {
      this.sunucuCSS2d.setSize(width, height);
    }

    if (this.aktifKamera) {
      this.aktifKamera.updateProjectionMatrix();
    }

    if (this._arModu?.aktif) {
      this._arModu.boyutGuncelle(width, height);
    }

    // 🚀 PERFORMANS: FikoSecililer canvas rect cache'ini invalidate et
    if (this.sahneler[0]?.secilenler) {
      this.sahneler[0].secilenler.invalidateCanvasRectCache();
    }

    // BİLGİ: Ekran boyutu değiştiğinde kontrol hızlarını güncelle
    if (this.aktifKontrol && typeof this.aktifKontrol.handleResize === 'function') {
      this.aktifKontrol.handleResize();
    }

    this.markNeedsRender();
  }

  hazirla(): void {
    THREE.Cache.enabled = true;

    // 🚀 WebGPU availability check (async olarak kontrol et)
    this.checkWebGPUAvailability();

    // 🎯 GPU Tier Detection & Optimized Renderer Creation
    // Try to use FikoRendererFactory for automatic GPU tier detection
    // Start with fallback renderer, then upgrade asynchronously
    this.sunucu = new THREE.WebGLRenderer({
      alpha: true,
      canvas: this.canvas.nativeElement,
      antialias: this.antiAlias,
      logarithmicDepthBuffer: true,
      preserveDrawingBuffer: false, // Performans için false
      failIfMajorPerformanceCaveat: false,
      powerPreference: "high-performance", // Performans modu
      precision: "highp",
      stencil: true,
    });
    setCanvasRenderer(this.sunucu);

    // GPU tier detection - optimize existing WebGL renderer settings based on GPU capability
    // Note: WebGPU renderer swap disabled - incompatible material system with existing scene
    FikoRendererFactory.createBestRenderer({
      canvas: this.canvas.nativeElement,
      antialias: this.antiAlias,
      alpha: true,
      preferWebGPU: false, // WebGPU uses node-based materials, not compatible with standard Three.js materials
      pixelRatio: Math.min(window.devicePixelRatio, 1.5),
    }).then((result) => {
      console.log(`[FikoYT] GPU detected: ${result.type}, tier: ${result.gpuTier}, maxTexture: ${result.capabilities.maxTextureSize}`);
      // Dispose the detection renderer (we keep the original one)
      result.renderer.dispose();
    }).catch(() => {});

    // WebGL2 Texture Format Fix
    // GL_INVALID_ENUM uyarısını sustur (bilinen Three.js WebGL2 uyumsuzluğu)
    // Not: console.warn override kaldırıldı - temiz konsol için

    // PERFORMANS: Gelişmiş Renderer Ayarları
    // 🔧 WebGPU uyumlu: shadowMap sadece WebGL'de mevcut
    if (this.sunucu.shadowMap) {
      this.sunucu.shadowMap.enabled = this.enableShadows;

      // PERFORMANS: Use cheaper shadow algorithm (PCFShadowMap instead of PCFSoftShadowMap)
      // PCFShadowMap: 4 texture lookups, PCFSoftShadowMap: 9 lookups
      this.sunucu.shadowMap.type = this.enableShadows ? THREE.PCFShadowMap : THREE.BasicShadowMap;
    }

    // PERFORMANS: Limit pixel ratio for better performance
    this.sunucu.setPixelRatio(Math.min(this.pixelRatio, 1.5)); // Max 1.5x instead of 2x
    this.sunucu.setSize(
      this.canvas.nativeElement.clientWidth,
      this.canvas.nativeElement.clientHeight
    );

    // PERFORMANS: Only sort when necessary (set to false for on-demand rendering)
    this.sunucu.sortObjects = this.renderMode === 'on-demand';
    this.sunucu.outputColorSpace = THREE.SRGBColorSpace;

    this._onContextLost = (e: Event) => {
      e.preventDefault();
      this._isDestroyed || this.nzNotification.warning('WebGL Context', '3D görüntüleyici yeniden başlatılıyor...');
    };
    this._onContextRestored = () => {
      this.sunucu?.setSize(
        this.canvas.nativeElement.clientWidth,
        this.canvas.nativeElement.clientHeight
      );
      this.markNeedsRender();
      this.nzNotification.success('WebGL Context', '3D görüntüleyici geri yüklendi');
    };
    const cvs = this.sunucu.domElement;
    cvs.addEventListener('webglcontextlost', this._onContextLost, false);
    cvs.addEventListener('webglcontextrestored', this._onContextRestored, false);

    // PERFORMANS: Tone mapping with moderate exposure
    this.sunucu.toneMapping = THREE.ACESFilmicToneMapping;
    this.sunucu.toneMappingExposure = 1.0;

    // PERFORMANS: Shadow map resolution optimization (WebGL only)
    if (this.enableShadows && this.sunucu.shadowMap) {
      // Use lower resolution shadow maps for better performance
      // Default is 512x512, reduce to 1024 for complex scenes
      this.sunucu.shadowMap.autoUpdate = false; // Manual control for better performance
    }

    // ULTRA PERFORMANS: Initialize advanced optimization systems
    this.initializeAdvancedOptimizations();

    // Kamera
    this.aktifKamera = new FikoOrthoKamera(this);
    
    // Kontroller - TypeScript FikoKontrolCad kullan (THREE.js single instance)
    this.aktifKontrol = new FikoKontrolCad(
      this.aktifKamera,
      this.sunucu.domElement
    );

    // 🎯 KULLANICI AYARI: Hassas pan kontrolü
    // Viewport boyutundan bağımsız, her zaman aynı hassasiyet
    if ((this.aktifKontrol as any)._basePanSpeed !== undefined) {
      // NOT: 0.2 = Çok hassas kontrol (kullanıcı talebi)
      // Değer zaten FikoKontrolCad constructor'da 0.2 olarak ayarlandı
      // Bu satır override etmemek için kaldırıldı veya aynı değere set edildi
      (this.aktifKontrol as any)._basePanSpeed = 0.2;
    }

    // İlk dinamik hız hesaplamasını yap
    if (typeof this.aktifKontrol.updateDynamicSpeeds === 'function') {
      this.aktifKontrol.updateDynamicSpeeds();
    }

    // 🚀 RENDER PERFORMANS: Kamera kontrolü event listener'ları
    // 'start' = Kullanıcı hareket ettirmeye başladı (mousedown, touchstart)
    // 'change' = Kamera değişti (her frame)
    // 'end' = Kullanıcı bıraktı (mouseup, touchend)
    this.aktifKontrol.addEventListener('start', this._onControlStart);
    this.aktifKontrol.addEventListener('change', this._onControlChange);
    this.aktifKontrol.addEventListener('end', this._onControlEnd);

    // Kamera change event'i için güvenli yöntem
    this.kameraChangeHandler = () => {
      if (this.aktifKamera) {
        this.aktifKamera.updateProjectionMatrix();
        this.markNeedsRender();
      }
    };
    
    // FikoOrthoKamera'nın kendi event sistemini kullan
    if (this.aktifKamera && typeof (this.aktifKamera as any).addEventListener === 'function') {
      try {
        (this.aktifKamera as any).addEventListener("change", this.kameraChangeHandler);
      } catch (e) {
        // Camera event listener setup failed
      }
    }

    // CSS3D Renderer
    this.sunucuCSS3d = new CSS3DRenderer();
    this.sunucuCSS3d.setSize(
      this.canvas.nativeElement.clientWidth,
      this.canvas.nativeElement.clientHeight
    );
    this.sunucuCSS3d.domElement.style.position = 'absolute';
    this.sunucuCSS3d.domElement.style.top = "0px";
    this.sunucuCSS3d.domElement.style.pointerEvents = "none";

    // Ensure all child elements also don't block pointer events
    const css3dStyle = document.createElement('style');
    css3dStyle.textContent = `
      #canvasCSS3d, #canvasCSS3d * { pointer-events: none !important; }
      #canvasCSS2d, #canvasCSS2d * { pointer-events: none !important; }
      #canvasCSS3d .fiko-smart-point-hover-area,
      #canvasCSS3d .fiko-smart-point-label,
      #canvasCSS3d .fiko-smart-point-tooltip,
      #canvasCSS3d [data-interactive="true"],
      #canvasCSS2d .fiko-smart-point-hover-area,
      #canvasCSS2d .fiko-smart-point-label,
      #canvasCSS2d .fiko-smart-point-tooltip,
      #canvasCSS2d [data-interactive="true"] { pointer-events: auto !important; }

      /* Smart Point hover area stili */
      .fiko-smart-point-hover-area {
        transition: transform 0.15s ease, box-shadow 0.15s ease;
      }
      .fiko-smart-point-hover-area:hover {
        transform: translate(-50%, -50%) scale(1.15);
        box-shadow: 0 0 12px rgba(255, 215, 0, 0.5);
      }
    `;
    this._css3dStyleEl = css3dStyle;
    document.head.appendChild(css3dStyle);

    this.canvasCSS3D.nativeElement.appendChild(this.sunucuCSS3d.domElement);
    this.css3dSahne = new FikoSahne(this);

    // CSS2D Renderer - FikoSmartPoint label/tooltip için
    this.sunucuCSS2d = new CSS2DRenderer();
    this.sunucuCSS2d.setSize(
      this.canvas.nativeElement.clientWidth,
      this.canvas.nativeElement.clientHeight
    );
    this.sunucuCSS2d.domElement.style.position = 'absolute';
    this.sunucuCSS2d.domElement.style.top = '0px';
    this.sunucuCSS2d.domElement.style.pointerEvents = 'none';
    this.sunucuCSS2d.domElement.id = 'canvasCSS2d';
    this.canvasCSS3D.nativeElement.appendChild(this.sunucuCSS2d.domElement);

    // Post-processing başlat
    // if (this.enablePostProcessing) {
    //   this.postProcessing.initialize(this.sunucu, this.css3dSahne, this.aktifKamera);
    // }

    this.markNeedsRender();
  }

  // ============================================================================
  // 🚀 WEBGPU RENDERER METHODS - Seçimli WebGPU/WebGL Dual Mode
  // ============================================================================

  /**
   * WebGPU kullanılabilirliğini kontrol et (async)
   */
  private async checkWebGPUAvailability(): Promise<void> {
    try {
      this.webGPUAvailable = await isWebGPUAvailable();
      if (this.webGPUAvailable) {
        const info = await getWebGPUInfo();
        // WebGPU mevcut
      }
    } catch (e) {
      this.webGPUAvailable = false;
    }
  }

  /**
   * 🚀 WebGPU Renderer'a geç
   * @returns Promise<boolean> - Başarılı ise true
   */
  async switchToWebGPU(): Promise<boolean> {
    if (this._useWebGPU) {
      return true;
    }

    if (!this.webGPUAvailable) {
      return false;
    }

    try {

      // 🔧 ÖNEMLİ: WebGL ve WebGPU aynı canvas'ı paylaşamaz!
      // Yeni bir canvas oluşturup swap yapmalıyız
      const oldCanvas = this.canvas.nativeElement;
      const parent = oldCanvas.parentElement;

      if (!parent) {
        return false;
      }

      // Eski renderer'ı dispose et
      const oldRenderer = this.sunucu;
      if (oldRenderer) {
        oldRenderer.dispose();
      }

      // Yeni canvas oluştur (aynı özelliklerle)
      const newCanvas = document.createElement('canvas');
      newCanvas.id = oldCanvas.id;
      newCanvas.className = oldCanvas.className;
      newCanvas.style.cssText = oldCanvas.style.cssText;
      newCanvas.width = oldCanvas.width;
      newCanvas.height = oldCanvas.height;

      // Eski canvas'ı kaldır, yenisini ekle
      parent.replaceChild(newCanvas, oldCanvas);

      // Canvas referansını güncelle (ElementRef için workaround)
      (this.canvas as any).nativeElement = newCanvas;

      // WebGPU Renderer oluştur (yeni canvas ile)
      this.webGPURenderer = await FikoWebGPURenderer.create({
        canvas: newCanvas,
        antialias: this.antiAlias,
        alpha: true,
        logarithmicDepthBuffer: true,
        powerPreference: 'high-performance'
      });

      if (!this.webGPURenderer.isWebGPU) {
        // WebGPU başarısız oldu, WebGL'i yeniden oluştur
        this.sunucu = new THREE.WebGLRenderer({
          canvas: newCanvas,
          antialias: this.antiAlias,
          alpha: true,
          logarithmicDepthBuffer: true,
          powerPreference: 'high-performance',
        });
        this._useWebGPU = false;
        setCanvasRenderer(this.sunucu);
        return false;
      }

      // Sunucu referansını güncelle (uyumluluk için)
      this.sunucu = this.webGPURenderer.renderer as THREE.WebGLRenderer;
      this._useWebGPU = true;
      setCanvasRenderer(this.sunucu);

      // Renderer ayarlarını uygula
      this.sunucu.setPixelRatio(Math.min(this.pixelRatio, 1.5));
      this.sunucu.setSize(
        newCanvas.clientWidth || newCanvas.width,
        newCanvas.clientHeight || newCanvas.height
      );
      this.sunucu.outputColorSpace = THREE.SRGBColorSpace;
      this.sunucu.toneMapping = THREE.ACESFilmicToneMapping;
      this.sunucu.toneMappingExposure = 1.0;

      // 🔧 Kontrolleri yeni canvas'a bağla
      if (this.aktifKontrol) {
        this._removeControlListeners();
        this.aktifKontrol.dispose();

        // Yeni kontrol oluştur
        this.aktifKontrol = new FikoKontrolCad(
          this.aktifKamera,
          newCanvas  // Yeni canvas'a bağla
        );

        this.aktifKontrol.addEventListener('start', this._onControlStart);
        this.aktifKontrol.addEventListener('change', this._onControlChangeLight);
        this.aktifKontrol.addEventListener('end', this._onControlEnd);
      }

      // Render'ı tetikle
      this.markNeedsRender();

      return true;
    } catch (e) {
      // Hata durumunda WebGL'i yeniden oluşturmaya çalış
      try {
        this.sunucu = new THREE.WebGLRenderer({
          canvas: this.canvas.nativeElement,
          antialias: this.antiAlias,
          alpha: true,
          logarithmicDepthBuffer: true,
          powerPreference: 'high-performance',
        });
        this._useWebGPU = false;
        setCanvasRenderer(this.sunucu);
      } catch (e2) {
        // Critical error
      }
      return false;
    }
  }

  /**
   * 🔶 WebGL Renderer'a geri dön
   */
  switchToWebGL(): void {
    if (!this._useWebGPU) {
      return;
    }

    // 🔧 WebGPU canvas'ından WebGL'e geçiş için yeni canvas gerekli
    const oldCanvas = this.canvas.nativeElement;
    const parent = oldCanvas.parentElement;

    // WebGPU renderer'ı dispose et
    this.webGPURenderer?.dispose();
    this.webGPURenderer = null;

    if (parent) {
      // Yeni canvas oluştur
      const newCanvas = document.createElement('canvas');
      newCanvas.id = oldCanvas.id;
      newCanvas.className = oldCanvas.className;
      newCanvas.style.cssText = oldCanvas.style.cssText;
      newCanvas.width = oldCanvas.width;
      newCanvas.height = oldCanvas.height;

      // Eski canvas'ı kaldır, yenisini ekle
      parent.replaceChild(newCanvas, oldCanvas);

      // Canvas referansını güncelle
      (this.canvas as any).nativeElement = newCanvas;
    }

    // Yeni WebGL renderer oluştur
    this.sunucu = new THREE.WebGLRenderer({
      alpha: true,
      canvas: this.canvas.nativeElement,
      antialias: this.antiAlias,
      logarithmicDepthBuffer: true,
      preserveDrawingBuffer: false,
      failIfMajorPerformanceCaveat: false,
      powerPreference: "high-performance",
      precision: "highp",
      stencil: true,
    });
    setCanvasRenderer(this.sunucu);

    // Ayarları uygula
    // 🔧 WebGPU uyumlu: shadowMap sadece WebGL'de mevcut
    if (this.sunucu.shadowMap) {
      this.sunucu.shadowMap.enabled = this.enableShadows;
      this.sunucu.shadowMap.type = this.enableShadows ? THREE.PCFShadowMap : THREE.BasicShadowMap;
    }
    this.sunucu.setPixelRatio(Math.min(this.pixelRatio, 1.5));
    this.sunucu.setSize(
      this.canvas.nativeElement.clientWidth,
      this.canvas.nativeElement.clientHeight
    );
    this.sunucu.outputColorSpace = THREE.SRGBColorSpace;
    this.sunucu.toneMapping = THREE.ACESFilmicToneMapping;
    this.sunucu.toneMappingExposure = 1.0;

    // 🔧 Kontrolleri yeni canvas'a bağla
    if (this.aktifKontrol) {
      this._removeControlListeners();
      this.aktifKontrol.dispose();

      this.aktifKontrol = new FikoKontrolCad(
        this.aktifKamera,
        this.canvas.nativeElement
      );

      this.aktifKontrol.addEventListener('start', this._onControlStart);
      this.aktifKontrol.addEventListener('change', this._onControlChangeLight);
      this.aktifKontrol.addEventListener('end', this._onControlEnd);
    }

    this._useWebGPU = false;
    this.markNeedsRender();
  }

  /**
   * Aktif renderer bilgisini al
   */
  getRendererInfo(): { type: 'webgpu' | 'webgl'; stats: any } {
    if (this._useWebGPU && this.webGPURenderer) {
      return {
        type: 'webgpu',
        stats: this.webGPURenderer.getStats()
      };
    }
    return {
      type: 'webgl',
      stats: {
        rendererType: 'webgl',
        drawCalls: this.sunucu?.info?.render?.calls || 0,
        triangles: this.sunucu?.info?.render?.triangles || 0,
        textures: this.sunucu?.info?.memory?.textures || 0,
        geometries: this.sunucu?.info?.memory?.geometries || 0
      }
    };
  }

  /**
   * WebGPU kullanılıyor mu?
   */
  get isUsingWebGPU(): boolean {
    return this._useWebGPU;
  }

  /**
   * WebGPU GPU Device'ını al (compute pipeline entegrasyonu için)
   */
  getWebGPUDevice(): GPUDevice | null {
    if (this._useWebGPU && this.webGPURenderer) {
      return this.webGPURenderer.gpuDevice;
    }
    return null;
  }

  // ============================================================================

  /**
   * ULTRA PERFORMANS: Initialize advanced optimization systems
   */
  private initializeAdvancedOptimizations(): void {
    try {
      // 1. BVH Raycast acceleration - DEVRE DIŞI (modül mevcut değil)
      // this.raycasterBVH = new FikoRaycastBVH();
      this.raycasterBVH = null;

      // 2. GPU Compute for vertex transformations - DEVRE DIŞI (modül mevcut değil)
      if (this.sunucu) {
        // this.gpuCompute = new FikoGPUCompute(this.sunucu, this.css3dSahne, this.aktifKamera);
        this.gpuCompute = null;

        // 🚀 GPU Compute Optimized - DEVRE DIŞI (modül mevcut değil)
        // this.gpuComputeOptimized = FikoGPUComputeOptimized.getInstance(...)
        this.gpuComputeOptimized = null;

        // 🚀 GPU Import Pipeline - WRL/STP dosya açma hızlandırması
        import('./IFikoThreeJS/fiko-performance/FikoGPUImportPipeline').then(({ getGPUImportPipeline }) => {
          const gpuImport = getGPUImportPipeline();
          if (gpuImport.initialize(this.sunucu)) {
            FikoThreeJsComponent.debugLog('✅ GPU Import Pipeline initialized');
          }
        }).catch(() => {});
      }

      // 3. Advanced LOD system - AKTİF (Streaming LOD ile)
      import('./IFikoThreeJS/fiko-performance/FikoStreamingLOD').then(({ FikoStreamingLODManager, FikoProgressiveMeshViewer }) => {
        this.streamingLODManager = new FikoStreamingLODManager();
        this.streamingLODManager.setCamera(this.aktifKamera);
        this.streamingLODManager.setMemoryBudget(512 * 1024 * 1024); // 512 MB
        FikoThreeJsComponent.debugLog('Streaming LOD Manager initialized (512 MB budget)');
      }).catch((e) => {
        // Streaming LOD yuklenemedi
      });

      // Fallback: Advanced LOD
      import('./IFikoThreeJS/fiko-performance/FikoAdvancedLOD').then(({ FikoAdvancedLOD }) => {
        this.advancedLOD = new FikoAdvancedLOD();
        if (this.sunucu) {
          this.advancedLOD.setRenderer(this.sunucu);
        }
        FikoThreeJsComponent.debugLog('✅ Advanced LOD System initialized');
      }).catch(() => {
        this.advancedLOD = null;
      });

      // 4. Occlusion culling - DEVRE DIŞI (modül mevcut değil)
      this.occlusionCulling = null;

      // 5. Render batch optimizer - DEVRE DIŞI (modül mevcut değil)
      // this.renderBatchOptimizer = new FikoRenderBatchOptimizer();
      this.renderBatchOptimizer = null;

      // 6. GPU Memory manager - DEVRE DIŞI (modül mevcut değil)
      // this.gpuMemoryManager = new FikoGPUMemoryManager(512);
      this.gpuMemoryManager = null;

      // 7. Geometry worker service
      this.geometryWorkerService = FikoGeometryWorkerService.getInstance();

      // TOP 5 ULTRA PERFORMANS: Latest optimization systems
      if (this.sunucu) {
        // 1. Dynamic Resolution Scaler - Initialize on-demand based on settings
        // Strategy: Only activate if FPS target is lower than display refresh or performance budget is tight
        if (this.threejsAyarlar.dynamicResolutionAktif) {
          try {
            this.dynamicResolutionScaler = new FikoDynamicResolutionScaler(this.sunucu);
            this.dynamicResolutionScaler.setTargetFPS(this.maxFPS);
            FikoThreeJsComponent.debugLog('Dynamic Resolution Scaler initialized (target FPS:', this.maxFPS + ')');
          } catch (e) {
            this.dynamicResolutionScaler = null;
          }
        } else {
          this.dynamicResolutionScaler = null;
        }

        // 2. Screen-Space Ambient Occlusion - DEVRE DIŞI (modül mevcut değil)
        const size = new THREE.Vector2();
        this.sunucu.getSize(size);
        // this.screenSpaceAO = new FikoScreenSpaceAO(this.sunucu, size.x, size.y);
        this.screenSpaceAO = null;

        // 3. Temporal Anti-Aliasing - DEVRE DIŞI (modül mevcut değil)
        // this.temporalAA = new FikoTemporalAA(...)
        this.temporalAA = null;

        // 🚀 PERFORMANS: CAD uygulaması için gereksiz sistemler DEVRE DIŞI
        // Particle, Skinning, Physics gibi sistemler CAD'de kullanılmıyor - LAZY LOADING'e taşındı

        // 4. GPU Particle System - DEVRE DIŞI (CAD'de gereksiz, on-demand başlatılır)
        this.gpuParticleCompute = null;

        // Object Pool Manager - DEVRE DIŞI (modül mevcut değil)
        // this.objectPoolManager = FikoObjectPoolManager.getInstance();
        this.objectPoolManager = null;

        // Texture Streaming - DEVRE DIŞI (küçük modeller için gereksiz)
        this.textureStreaming = null;

        // Shadow Optimizer - KALDIRILDI (modül mevcut değil)

        // 🚀 PERFORMANS: LOD sistemleri - sadece Advanced LOD aktif
        // Aggressive LOD yalnızca çok büyük modellerde manuel olarak aktifleştirilebilir
        this.aggressiveLOD = null; // On-demand başlatılır

        // Compute Worker Pool - DEVRE DIŞI (modül mevcut değil)
        // this.computeWorkerPool = new FikoComputeWorkerPool(2);
        this.computeWorkerPool = null;

        // Frame Budget - DEVRE DIŞI (modül mevcut değil)
        // this.frameBudget = new FikoFrameBudget(60);
        this.frameBudget = null;

        // 🚀 PERFORMANS: Instanced Optimizer - aktif (draw call azaltma için faydalı)
        if (FikoInstancedOptimizer) {
          this.instancedOptimizer = new FikoInstancedOptimizer();
        } else {
          this.instancedOptimizer = null;
        }

        // GPU Driven Renderer - DEVRE DIŞI (karmaşık, çoğu durumda gereksiz)
        this.gpuDrivenRenderer = null;

        // Visibility Precompute - DEVRE DIŞI (occlusion culling yeterli)
        this.visibilityPrecompute = null;

        // Aggressive Merging - KALDIRILDI (modül mevcut değil)

        // Virtual Textures - On-demand başlatılır
        if (this.threejsAyarlar.virtualTexturesAktif) {
          this.virtualTextures = new FikoVirtualTextures();
        } else {
          this.virtualTextures = null;
        }

        // Neural Compression - DEVRE DIŞI (modül mevcut değil)
        // this.neuralCompression = new FikoNeuralCompression();
        this.neuralCompression = null;

        // 🚀 PERFORMANS: GPU COMPUTE sistemleri - CAD için gereksiz olanlar DEVRE DIŞI
        // Tümü null başlatılıyor, on-demand ayarlar ile aktifleştirilebilir

        // GPU Physics - DEVRE DIŞI (CAD'de physics gereksiz)
        this.gpuPhysicsCompute = null;

        // GPU Frustum Culling - DEVRE DIŞI (CPU frustum yeterli)
        this.gpuFrustumCulling = null;

        // GPU Skinning - DEVRE DIŞI (CAD'de skeleton animasyon yok)
        this.gpuSkinning = null;

        // GPU Particle Advection - DEVRE DIŞI (CAD'de particle flow yok)
        this.gpuParticleAdvection = null;

        // GPU Texture Filtering - On-demand başlatılır
        this.gpuTextureFiltering = null;

        // 9. GPU Async Compute Pipeline - Pipeline optimization
        // DISABLED: Potential feedback loop issues with async rendering
        // this.gpuAsyncComputePipeline = new FikoGPUAsyncComputePipeline(this.sunucu);

        // 10. GPU Stream Output List - Indirect rendering optimization
        // DISABLED: WebGL buffer binding issues with indirect rendering
        // this.gpuStreamOutputList = new FikoGPUStreamOutputList(this.sunucu, 500000); // Reduced from 1M

        // 🚀 11. WebGPU Camera Operations - Pan/Rotate/Zoom GPU acceleration
        // Frustum culling, LOD distance, headlight updates all on GPU
        if (this.webgpuCameraOpsEnabled) {
          FikoWebGPUCameraOps.initialize().then(instance => {
            this.webgpuCameraOps = instance;
            if (instance.available) {
              FikoThreeJsComponent.debugLog('WebGPU Camera Ops initialized - GPU-accelerated pan/rotate/zoom');
            } else {
              FikoThreeJsComponent.debugLog('WebGPU Camera Ops: WebGPU not available, using CPU fallback');
            }
          }).catch(e => {
            // WebGPU Camera Ops yuklenemedi
          });
        }

        // 🚀 12. WebGPU Lighting - Clustered Forward+ & GPU Headlight Updates
        // Light clustering for 100+ lights, batch headlight updates on GPU
        if (this.webgpuLightingEnabled) {
          FikoWebGPULighting.initialize().then(instance => {
            this.webgpuLighting = instance;
            if (instance.available) {
              FikoThreeJsComponent.debugLog('WebGPU Lighting initialized - Clustered Forward+ active');
              // Also init FikoIsikYonsel static WebGPU
              FikoIsikYonsel.initWebGPULighting();
            } else {
              FikoThreeJsComponent.debugLog('WebGPU Lighting: WebGPU not available, using CPU fallback');
            }
          }).catch(e => {
            // WebGPU Lighting yuklenemedi
          });
        }

        // 🚀🚀🚀 13. CATIA-LEVEL Performance Integration (FAZA 4)
        // HiZ Occlusion, View-Dependent LOD, Advanced Edge Detection, Memory Pooling
        if (this.performanceIntegrationEnabled) {
          // Parametre sırası: camera, renderer, scene
          const perfIntegration = getPerformanceIntegration(this.aktifKamera, this.sunucu, this.sahneler[0]);
          if (perfIntegration) {
            perfIntegration.init().then(() => {
              this.performanceIntegration = perfIntegration;
              FikoThreeJsComponent.debugLog('🚀 CATIA-Level Performance Integration initialized');
              FikoThreeJsComponent.debugLog('   - HiZ Occlusion Culling (100M+ triangles)');
              FikoThreeJsComponent.debugLog('   - View-Dependent Streaming LOD');
              FikoThreeJsComponent.debugLog('   - Advanced Edge Detection');
              FikoThreeJsComponent.debugLog('   - Memory Pooling (zero allocation/frame)');
              FikoThreeJsComponent.debugLog('   - Dynamic Batching (draw call reduction)');
            }).catch(e => {
              FikoThreeJsComponent.debugLog('Performance Integration partial init:', e);
            });
          }
        }
      }

    } catch (error) {
      // Advanced optimizations partial init error
    }
  }

  /**
   * 🚀 WebGPU Camera Ops: Build object bounds cache for GPU culling
   * Called when scene changes, caches bounding spheres for all visible meshes
   * 🚀🚀 PERFORMANS: Visible olmayan nesneler TAMAMEN atlanır (parent dahil)
   */
  private buildObjectBoundsCache(): void {
    this.cachedObjectBounds = [];

    if (this.sahneler.length === 0) return;

    let objectIndex = 0;
    for (const sahne of this.sahneler) {
      // 🚀 PERFORMANS: Custom traverse - visible=false olan branch'leri TAMAMEN atla
      this.traverseVisibleOnly(sahne, (obj: THREE.Object3D) => {
        if (obj instanceof THREE.Mesh && obj.geometry) {
          // Get or compute bounding sphere
          if (!obj.geometry.boundingSphere) {
            obj.geometry.computeBoundingSphere();
          }

          const sphere = obj.geometry.boundingSphere;
          if (sphere && sphere.radius > 0) {
            // Transform center to world space
            const worldCenter = sphere.center.clone();
            obj.localToWorld(worldCenter);

            // Scale radius by object's max scale
            const scale = obj.matrixWorld.getMaxScaleOnAxis();

            this.cachedObjectBounds.push({
              center: worldCenter,
              radius: sphere.radius * scale,
              objectIndex: objectIndex++,
              object: obj
            });
          }
        }
      });
    }

    this.objectBoundsCacheValid = true;
  }

  /**
   * 🚀🚀 PERFORMANS: Visible-only traverse - invisible branch'leri TAMAMEN atlar
   * Normal traverse() tüm children'a girer, bu versiyon visible=false görünce durur
   */
  private traverseVisibleOnly(obj: THREE.Object3D, callback: (obj: THREE.Object3D) => void): void {
    // 🚀 EARLY EXIT: Invisible ise kendisi VE tüm children atlanır
    if (!obj.visible) return;

    callback(obj);

    const children = obj.children;
    const len = children.length;
    for (let i = 0; i < len; i++) {
      this.traverseVisibleOnly(children[i], callback);
    }
  }

  /**
   * 🚀 WebGPU Camera Ops: Invalidate bounds cache when scene changes
   */
  invalidateObjectBoundsCache(): void {
    this.objectBoundsCacheValid = false;
  }

  /**
   * 🚀 WebGPU Camera Ops: Update camera operations on GPU
   * Called during interaction for GPU-accelerated frustum culling and LOD
   */
  private async updateCameraOpsGPU(): Promise<void> {
    if (!this.webgpuCameraOps || !this.aktifKamera) return;

    // Rebuild cache if invalid
    if (!this.objectBoundsCacheValid) {
      this.buildObjectBoundsCache();
    }

    if (this.cachedObjectBounds.length === 0) return;

    // Get screen size
    const width = this.canvas.nativeElement.clientWidth;
    const height = this.canvas.nativeElement.clientHeight;

    // Run GPU camera ops
    const result = await this.webgpuCameraOps.computeCameraOps(
      this.cachedObjectBounds,
      this.aktifKamera,
      [width, height]
    );

    if (result) {
      this.lastCameraOpsResult = result;

      // 🚀🚀 PERFORMANS: Apply frustum culling via frustumCulled flag (NOT visible!)
      // visible = kullanıcı kontrolü, frustumCulled = render optimization
      // Three.js frustumCulled=true olan nesneleri otomatik atlar
      for (let i = 0; i < this.cachedObjectBounds.length; i++) {
        const obj = this.cachedObjectBounds[i].object;
        const inFrustum = result.visibleIndices[i] === 1;

        // 🚀 PERFORMANS: Sadece frustumCulled değiştir, visible'a DOKUNMA
        // frustumCulled=false demek "render et", frustumCulled=true demek "GPU frustum test yap"
        // Biz manuel test yaptık, bu yüzden:
        // - In frustum -> layers.enable(0) ile render
        // - Out of frustum -> layers.disable(0) ile skip
        if (inFrustum) {
          obj.layers.enable(0);
        } else {
          obj.layers.disable(0);
        }
      }
    }
  }

  /**
   * Get all optimization systems for external access
   */
  public getOptimizationSystems() {
    return {
      // ULTRA PERFORMANS: Original 7 systems
      raycasterBVH: this.raycasterBVH,
      gpuCompute: this.gpuCompute,
      advancedLOD: this.advancedLOD,
      occlusionCulling: this.occlusionCulling,
      renderBatchOptimizer: this.renderBatchOptimizer,
      gpuMemoryManager: this.gpuMemoryManager,
      geometryWorkerService: this.geometryWorkerService,

      // TOP 5 ULTRA PERFORMANS: Latest systems
      dynamicResolutionScaler: this.dynamicResolutionScaler,
      screenSpaceAO: this.screenSpaceAO,
      temporalAA: this.temporalAA,
      gpuParticleCompute: this.gpuParticleCompute,

      // NEXT 5 ULTRA PERFORMANS: Second wave systems
      objectPoolManager: this.objectPoolManager,
      meshCompressor: FikoMeshCompressor, // Static utility
      textureStreaming: this.textureStreaming,

      // EXTREME 10 MORE ULTRA PERFORMANS: Third wave systems
      aggressiveLOD: this.aggressiveLOD,
      computeWorkerPool: this.computeWorkerPool,
      zPrepass: this.zPrepass,
      frameBudget: this.frameBudget,
      instancedOptimizer: this.instancedOptimizer,
      gpuDrivenRenderer: this.gpuDrivenRenderer,
      visibilityPrecompute: this.visibilityPrecompute,
      virtualTextures: this.virtualTextures,
      neuralCompression: this.neuralCompression,

      // GPU COMPUTE EXTREME 10 MORE OPTIMIZATIONS: Fourth wave systems
      gpuPhysicsCompute: this.gpuPhysicsCompute,
      gpuFrustumCulling: this.gpuFrustumCulling,
      gpuOcclusionQueries: this.gpuOcclusionQueries,
      gpuTransformFeedback: this.gpuTransformFeedback,
      gpuSkinning: this.gpuSkinning,
      gpuParticleAdvection: this.gpuParticleAdvection,
      gpuTextureFiltering: this.gpuTextureFiltering,
      gpuTileDeferredRendering: this.gpuTileDeferredRendering,
      gpuAsyncComputePipeline: this.gpuAsyncComputePipeline,
      gpuStreamOutputList: this.gpuStreamOutputList
    };
  }

  // Render İşaretleme (On-Demand Rendering)
  private _isRendering = false; // Recursion guard

  /**
   * 🚀 PERFORMANS OPT #36: RAF-batched On-Demand Rendering
   * Aynı frame'de birden fazla markNeedsRender() çağrısı tek render'a birleştirilir
   * - CPU: ~90% idle reduction (no redundant renders)
   * - Battery: Significant power savings on mobile
   */
  markNeedsRender(): void {
    this.needsRender = true;

    // 🚀 RAF batching: Aynı frame'deki tüm çağrıları birleştir
    if (this.renderMode === 'on-demand' && !this.renderScheduled && !this.animationFrameId) {
      this.renderScheduled = true;

      requestAnimationFrame(() => {
        this.renderScheduled = false;

        // 🚀 Double-check: Hala render gerekli mi?
        if (this.needsRender && !this._isRendering && !this._isDestroyed) {
          this._isRendering = true;
          try {
            this.ngZone.runOutsideAngular(() => this.sun());
            this.needsRender = false;
          } finally {
            this._isRendering = false;
          }
        }
      });
    }
  }

  /**
   * 🚀 RENDER PERFORMANS: Camera interaction start handler
   * Marks interaction state for optimizations (LOD, culling, post-processing)
   */
  private onCameraInteractionStart(): void {
    if (!this.isInteracting) {
      this.isInteracting = true;

      // ⚠️ PIXEL RATIO DEĞİŞTİRME KALDIRILDI
      // Çizgileri bozduğu için pixel ratio sabit kalıyor
      // Performans artışı diğer optimizasyonlardan geliyor:
      // - Post-processing skip
      // - Shadow map skip
      // - Her frame LOD/Culling
      // - Damping ile smooth hareket

      // Continuous rendering başlat
      if (this.renderMode === 'on-demand') {
        this.surekliCiz();
      }
    }

    // Timer'ı sıfırla (kullanıcı hala hareket ettiriyor)
    if (this.interactionEndTimer) {
      clearTimeout(this.interactionEndTimer);
      this.interactionEndTimer = null;
    }
  }

  /**
   * 🚀 RENDER PERFORMANS: Camera change handler
   * Called every frame during interaction - keeps timer reset
   */
  private onCameraChange(): void {
    // Her frame'de interaction timer'ı sıfırla
    this.onCameraInteractionStart();

    // Fast render tetikle
    this.markNeedsRender();
  }

  /**
   * 🚀 RENDER PERFORMANS: Camera interaction end handler
   * Restores full quality render (post-processing, shadows)
   */
  private onCameraInteractionEnd(): void {
    // Wait 150ms after interaction ends (debounce)
    if (this.interactionEndTimer) {
      clearTimeout(this.interactionEndTimer);
    }

    this.interactionEndTimer = setTimeout(() => {
      if (this._isDestroyed) return;
      this.isInteracting = false;

      // ⚠️ PIXEL RATIO RESTORE KALDIRILDI - Zaten sabit
      // Pixel ratio her zaman normalPixelRatio'da kalıyor

      // 🚀 Trigger full quality final render
      // Post-processing ve shadow'lar tekrar aktif olacak
      this.ngZone.runOutsideAngular(() => this.sun());

      // ⚠️ Continuous rendering DURDURMA - Damping devam ediyor
      // Damping bittikten sonra kamera 'change' event'i duracak
      // O zaman zaten on-demand mode devreye girecek
    }, 150);
  }

  /**
   * 🚀 DEPRECATED: Use onCameraInteractionEnd instead
   * Kept for backward compatibility
   */
  private onInteractionEnd(): void {
    this.onCameraInteractionEnd();
  }

  private _removeControlListeners(): void {
    if (!this.aktifKontrol) return;
    this.aktifKontrol.removeEventListener('start', this._onControlStart);
    this.aktifKontrol.removeEventListener('change', this._onControlChange);
    this.aktifKontrol.removeEventListener('change', this._onControlChangeLight);
    this.aktifKontrol.removeEventListener('end', this._onControlEnd);
  }

  /**
   * 🚀 ULTRA PERFORMANS: Configure interaction quality settings
   * @param options - Configuration object
   */
  public configureInteractionQuality(options: {
    enabled?: boolean;
    normalPixelRatio?: number;
    interactionPixelRatio?: number;
  }): void {
    if (options.enabled !== undefined) {
      this.useInteractionQuality = options.enabled;
    }
    if (options.normalPixelRatio !== undefined) {
      this.normalPixelRatio = Math.max(0.5, Math.min(2.0, options.normalPixelRatio));
    }
    if (options.interactionPixelRatio !== undefined) {
      this.interactionPixelRatio = Math.max(0.25, Math.min(1.0, options.interactionPixelRatio));
    }
  }

  /**
   * 🚀 ULTRA PERFORMANS: Configure camera control inertia
   * @param options - Inertia configuration
   */
  public configureCameraInertia(options: {
    enabled?: boolean;
    friction?: number;
    minVelocity?: number;
  }): void {
    if (this.aktifKontrol && typeof this.aktifKontrol.configureInertia === 'function') {
      this.aktifKontrol.configureInertia(options);
    }
  }

  /**
   * 🚀 ULTRA PERFORMANS: Configure predictive camera movement
   * @param options - Predictive configuration
   */
  public configurePredictiveCamera(options: {
    enabled?: boolean;
    factor?: number;
    bufferSize?: number;
  }): void {
    if (this.aktifKontrol && typeof this.aktifKontrol.configurePredictive === 'function') {
      this.aktifKontrol.configurePredictive(options);
    }
  }

  /**
   * 🚀 ULTRA PERFORMANS: Get camera control performance stats
   */
  public getCameraPerformanceStats(): any {
    if (this.aktifKontrol && typeof this.aktifKontrol.getPerformanceStats === 'function') {
      return {
        ...this.aktifKontrol.getPerformanceStats(),
        avgRenderTime: this.avgRenderTime.toFixed(2) + 'ms',
        interactionPixelRatio: (this.interactionPixelRatio * 100).toFixed(0) + '%',
        isInteracting: this.isInteracting
      };
    }
    return {
      avgRenderTime: this.avgRenderTime.toFixed(2) + 'ms',
      interactionPixelRatio: (this.interactionPixelRatio * 100).toFixed(0) + '%',
      isInteracting: this.isInteracting
    };
  }

  /**
   * 🚀 PERFORMANS: Register headlight for cached updates (avoids scene traversal)
   */
  public registerHeadlight(light: THREE.Light): void {
    if (!this.cachedHeadlights.includes(light)) {
      this.cachedHeadlights.push(light);
    }
  }

  /**
   * 🚀 PERFORMANS: Unregister headlight when removed from scene
   */
  public unregisterHeadlight(light: THREE.Light): void {
    const index = this.cachedHeadlights.indexOf(light);
    if (index > -1) {
      this.cachedHeadlights.splice(index, 1);
    }
  }

  /**
   * 🚀 ULTRA PERFORMANS: Ultra-fast render for camera interactions
   * Skips all expensive optimization systems - only does essential rendering
   * Target: <5ms total frame time during pan/rotate/zoom
   *
   * Features:
   * - Reduced pixel ratio during interaction (75% of normal)
   * - Skip all GPU compute systems
   * - Skip LOD updates, culling, post-processing
   * - Cached headlight updates only
   * - Adaptive quality based on actual render time
   */
  sunHizli(): void {
    const startTime = performance.now();

    // 🚀 RENDER PERFORMANS: Update camera controls for smooth damping
    // Damping (inertia) sistemi aktif olduğunda her frame çağrılmalı
    if (this.aktifKontrol && typeof this.aktifKontrol.update === 'function') {
      this.aktifKontrol.update();
    }

    // 🚀 PERFORMANS: Pixel ratio sabit - değiştirilmiyor
    // (Kullanıcı isteği: "pixel ratio sabit kalsın şimdilik")

    // 🚀 PERFORMANS: Update cached headlights only (no scene traversal)
    // 🚀🚀 PERFORMANS: Invisible ışıkları ATLA
    const headlightCount = this.cachedHeadlights.length;
    for (let i = 0; i < headlightCount; i++) {
      const light = this.cachedHeadlights[i] as any;
      if (!light.visible) continue;  // 🚀 INVISIBLE IŞIKLARI ATLA
      if (light.kameraTakipAktif && light.updateLightPosition) {
        light.updateLightPosition();
      }
    }

    // 🚀 PERFORMANS: Fallback to scene traversal only if no cached headlights
    // 🚀🚀 PERFORMANS: Visible-only traverse kullan
    if (headlightCount === 0 && this.sahneler.length > 0) {
      // One-time cache population
      for (let s = 0; s < this.sahneler.length; s++) {
        this.traverseVisibleOnly(this.sahneler[s], obj => {
          if (obj.type === 'FikoIsikYonsel') {
            this.cachedHeadlights.push(obj as THREE.Light);
            const l = obj as any;
            if (l.kameraTakipAktif && l.updateLightPosition) {
              l.updateLightPosition();
            }
          }
        });
      }
    }

    // 🚀 ULTRA PERFORMANS: Update camera control GPU load factor
    if (this.aktifKontrol && typeof this.aktifKontrol.setGPULoadFactor === 'function') {
      this.aktifKontrol.setGPULoadFactor(this.avgRenderTime);
    }

    // 🚀 PERFORMANS: SKIP ALL GPU OPTIMIZATION SYSTEMS DURING INTERACTION
    // This is the key to smooth pan/rotate/zoom

    // 🚀🚀🚀 PERFORMANS: Disable sorting and shadows for MAXIMUM speed
    const prevSortObjects = this.sunucu.sortObjects;
    // 🔧 WebGPU uyumlu: shadowMap sadece WebGL'de mevcut
    const hasShadowMap = this.sunucu.shadowMap && 'autoUpdate' in this.sunucu.shadowMap;
    const prevShadowAutoUpdate = hasShadowMap ? this.sunucu.shadowMap.autoUpdate : false;
    this.sunucu.sortObjects = false;  // 🚀 Skip depth sorting
    if (hasShadowMap) {
      this.sunucu.shadowMap.autoUpdate = false;  // 🚀 Skip shadow updates
    }

    // 🚀 PERFORMANS: Direct render - no culling, no LOD updates, no post-processing
    // Unrolled for common cases (1-2 scenes)
    try {
      const sceneCount = this.sahneler.length;
      if (sceneCount === 1) {
        if (this.sahneler[0].visible) {
          this.sunucu.render(this.sahneler[0], this.aktifKamera);
        }
      } else if (sceneCount === 2) {
        if (this.sahneler[0].visible) this.sunucu.render(this.sahneler[0], this.aktifKamera);
        if (this.sahneler[1].visible) this.sunucu.render(this.sahneler[1], this.aktifKamera);
      } else {
        for (let i = 0; i < sceneCount; i++) {
          if (this.sahneler[i].visible) {
            this.sunucu.render(this.sahneler[i], this.aktifKamera);
          }
        }
      }
    } finally {
      this.sunucu.sortObjects = prevSortObjects;
      if (hasShadowMap) {
        this.sunucu.shadowMap.autoUpdate = prevShadowAutoUpdate;
      }
    }

    // 🔥 FIX: FikoEksen objelerini en üstte render et (overlay pass)
    // sortObjects=false olduğunda renderOrder çalışmıyor, bu yüzden ayrı pass gerekli
    this.renderEksenOverlay();

    // 🚀 PERFORMANS: CSS3D should update during interaction for labels
    if (this.css3dSahne) {
      this.sunucuCSS3d.render(this.css3dSahne, this.aktifKamera);
    }

    // 🚀 CSS2D Render - FikoSmartPoint label/tooltip için
    if (this.sunucuCSS2d && this.sahneler?.[0]) {
      this.sunucuCSS2d.render(this.sahneler[0], this.aktifKamera);
    }

    // 🎯 FIX: TWEEN update - Her zaman çağır (getAll() bazı versiyonlarda güvenilir değil)
    if (typeof TWEEN !== 'undefined') {
      TWEEN.update(performance.now());
    }

    // 🚀 ULTRA PERFORMANS: Track render time for stats
    const endTime = performance.now();
    const renderTime = endTime - startTime;
    this.avgRenderTime = this.avgRenderTime * this.renderTimeSmoothing +
                         renderTime * (1 - this.renderTimeSmoothing);

    // 🚀 PERFORMANS: Pixel ratio sabit - adaptive quality devre dışı
    // (Kullanıcı isteği: "pixel ratio sabit kalsın şimdilik")

    this.renderComplete.emit();
  }

  // 🔥 FikoEksen overlay render için cache ve sahne
  private _eksenOverlaySahne: THREE.Scene | null = null;
  private _eksenOverlayCache: THREE.Object3D[] = [];
  private _eksenOverlayCacheDirty = true;

  // 🚀 PERFORMANS: Pre-allocated buffers for per-frame operations (zero allocation)
  private _visibleEksenBuffer: any[] = [];
  private _eksenParentMap = new Map<THREE.Object3D, THREE.Object3D | null>();
  private _lightDataBuffer: Array<{
    position: THREE.Vector3;
    offsetDistance: number;
    manualOffset: THREE.Vector3;
    isActive: boolean;
    targetPosition: THREE.Vector3;
  }> = [];

  /** FikoEksen cache'ini invalidate et (sahne değiştiğinde çağrılmalı) */
  invalidateEksenCache(): void {
    this._eksenOverlayCacheDirty = true;
  }

  /**
   * 🔥 FikoEksen objelerini overlay olarak render et
   * sortObjects=false olduğunda renderOrder çalışmıyor
   * Bu metot depth buffer'ı temizleyip sadece FikoEksen'leri render eder
   */
  private renderEksenOverlay(): void {
    if (!this.sahneler?.length) return;

    // 🚀 PERFORMANS: Lazy init overlay sahne
    if (!this._eksenOverlaySahne) {
      this._eksenOverlaySahne = new THREE.Scene();
    }

    // 🚀 PERFORMANS: Cache invalidate olmuşsa yeniden tara
    if (this._eksenOverlayCacheDirty) {
      this._eksenOverlayCache = [];
      for (const sahne of this.sahneler) {
        sahne.traverse((obj: THREE.Object3D) => {
          if ((obj as any).type === 'FikoEksen') {
            this._eksenOverlayCache.push(obj);
          }
        });
      }
      this._eksenOverlayCacheDirty = false;
    }

    // 🚀 PERFORMANS: Zero-allocation visible eksen filtering
    this._visibleEksenBuffer.length = 0;
    for (let i = 0; i < this._eksenOverlayCache.length; i++) {
      if (this._eksenOverlayCache[i].visible) {
        this._visibleEksenBuffer.push(this._eksenOverlayCache[i]);
      }
    }
    const visibleEksenler = this._visibleEksenBuffer;
    if (visibleEksenler.length === 0) return;

    // Depth buffer'ı temizle, color buffer'ı koru
    this.sunucu.clearDepth();

    // 🚀 PERFORMANS: Reuse parentMap, zero allocation
    this._eksenParentMap.clear();

    for (const eksen of visibleEksenler) {
      this._eksenParentMap.set(eksen, eksen.parent);
      this._eksenOverlaySahne.add(eksen);
    }

    // Eksen sahnesini render et (autoClear: false zaten depth temizlendi)
    const prevAutoClear = this.sunucu.autoClear;
    this.sunucu.autoClear = false;
    this.sunucu.render(this._eksenOverlaySahne, this.aktifKamera);
    this.sunucu.autoClear = prevAutoClear;

    // Eksenleri orijinal parent'larına geri ekle
    for (const eksen of visibleEksenler) {
      const originalParent = this._eksenParentMap.get(eksen);
      if (originalParent) {
        originalParent.add(eksen);
      }
    }

    // Overlay sahneyi temizle
    this._eksenOverlaySahne.clear();
  }

  // Tek Frame Çizimi (On-Demand) - Full quality with all optimizations
  // 🚀 ULTRA PERFORMANS: Optimized for native-level performance
  sun(): void {
    const _arKameraSwap = this._arModu?.aktif ? this._arModu.getARKamera() : null;
    const _orthoKameraRef = this.aktifKamera;
    if (_arKameraSwap) (this as any).aktifKamera = _arKameraSwap;

    // GPU bellek izleme (her 60 frame'de bir)
    if (++this._memoryCheckCounter % 60 === 0) {
      const info = this.sunucu?.info;
      if (info) {
        const geoCount = info.memory?.geometries || 0;
        const texCount = info.memory?.textures || 0;
        const renderInfo = info.render;

        // gpuBilgi güncelle - dış bileşenler okuyabilir
        this.gpuBilgi.geometries = geoCount;
        this.gpuBilgi.textures = texCount;
        this.gpuBilgi.drawCalls = renderInfo?.calls || 0;
        this.gpuBilgi.triangles = renderInfo?.triangles || 0;
        this.gpuBilgi.points = renderInfo?.points || 0;
        this.gpuBilgi.lines = renderInfo?.lines || 0;

        // Bellek sızıntısı uyarıları (30s throttle)
        const now = performance.now();
        if (geoCount > 5000 && now - this._lastLeakWarnTime > 30000) {
          console.warn('[FikoYT-3D] Olası bellek sızıntısı: geometri sayısı çok yüksek:', geoCount);
          this._lastLeakWarnTime = now;
        }
        if (texCount > 1000 && now - this._lastLeakWarnTime > 30000) {
          console.warn('[FikoYT-3D] Olası bellek sızıntısı: texture sayısı çok yüksek:', texCount);
          this._lastLeakWarnTime = now;
        }

        // Artış takibi
        if (this._lastGeometryCount === 0) {
          // İlk ölçüm - sadece kaydet, uyarı verme
          this._lastGeometryCount = geoCount;
          this._lastTextureCount = texCount;
        } else {
          if (geoCount > this._lastGeometryCount + 10 && now - this._lastLeakWarnTime > 30000) {
            console.warn('[FikoYT-3D] GPU bellek artışı tespit edildi:', {
              geometries: `${this._lastGeometryCount} → ${geoCount}`,
              textures: `${this._lastTextureCount} → ${texCount}`,
              drawCalls: renderInfo?.calls
            });
            this._lastLeakWarnTime = now;
          }
          this._lastGeometryCount = geoCount;
          this._lastTextureCount = texCount;
        }
      }
    }

    // 🚀 RENDER PERFORMANS: Update camera controls for smooth damping
    // Damping (inertia) sistemi aktif olduğunda her frame çağrılmalı
    if (this.aktifKontrol && typeof this.aktifKontrol.update === 'function') {
      this.aktifKontrol.update();
    }

    // 🚀🚀🚀 CATIA-LEVEL PERFORMANCE: Update HiZ Occlusion, LOD, Edge Detection
    // Frame time calculation for deltaTime
    const currentTime = performance.now();
    const deltaTime = (currentTime - this.lastPerformanceUpdateTime) / 1000; // seconds
    this.lastPerformanceUpdateTime = currentTime;

    if (this.performanceIntegration && deltaTime > 0 && deltaTime < 1) {
      this.performanceIntegration.update(deltaTime);
    }

    // 🚀 EARLY EXIT: Skip heavy processing during interaction
    // This check MUST be first for maximum performance
    if (this.isInteracting) {
      // 🚀 PERFORMANS: Cache boşsa, ilk interaction'da traverse ile doldur (one-time)
      // 🚀🚀 PERFORMANS: Sadece VISIBLE ışıkları cache'le
      let headlightCount = this.cachedHeadlights.length;
      if (headlightCount === 0 && this.sahneler.length > 0) {
        for (let s = 0; s < this.sahneler.length; s++) {
          // 🚀 PERFORMANS: Visible-only traverse kullan
          this.traverseVisibleOnly(this.sahneler[s], obj => {
            // FikoIsikYonsel veya DirectionalLight with kameraTakipAktif - SADECE VISIBLE
            if ((obj.type === 'FikoIsikYonsel' || ((obj as any).kameraTakipAktif !== undefined && obj instanceof THREE.DirectionalLight))) {
              this.cachedHeadlights.push(obj as THREE.Light);
            }
          });
        }
        headlightCount = this.cachedHeadlights.length;
      }

      // 🚀🚀🚀 WEBGPU CAMERA OPS: GPU-accelerated headlight update
      // Try GPU path first for headlights during interaction
      if (this.webgpuCameraOps && this.webgpuCameraOps.available && headlightCount > 0 && this.aktifKamera) {
        // 🚀 PERFORMANS: Reuse lightData buffer, zero allocation
        this._lightDataBuffer.length = 0;

        for (let i = 0; i < headlightCount; i++) {
          const light = this.cachedHeadlights[i] as any;
          // 🚀 PERFORMANS: Invisible ışıkları atla
          if (!light.visible) continue;
          this._lightDataBuffer.push({
            position: light.position,
            offsetDistance: light.kameraOffsetUzakligi || 20,
            manualOffset: light.kameraManuelOffset || FikoThreeJsComponent._defaultLightOffset,
            isActive: light.kameraTakipAktif || false,
            targetPosition: light.target?.position || FikoThreeJsComponent._defaultLightTarget
          });
        }

        // Non-blocking GPU headlight update
        this.webgpuCameraOps.computeHeadlightPositions(
          this._lightDataBuffer,
          this.aktifKamera.position,
          this.aktifKamera.quaternion
        ).then(result => {
          if (result) {
            // Apply results to lights
            for (let i = 0; i < headlightCount; i++) {
              const light = this.cachedHeadlights[i] as any;
              if (light.kameraTakipAktif) {
                const offset = i * 3;
                light.position.set(
                  result.positions[offset],
                  result.positions[offset + 1],
                  result.positions[offset + 2]
                );
                if (light.target) {
                  light.target.position.set(
                    result.targetPositions[offset],
                    result.targetPositions[offset + 1],
                    result.targetPositions[offset + 2]
                  );
                  light.target.updateMatrixWorld();
                }
                light.updateMatrixWorld();
              }
            }
          }
        }).catch(() => {
          // GPU failed, already have CPU fallback below
        });

        // 🚀 WEBGPU: Also trigger GPU frustum culling during interaction (non-blocking)
        if (this.cachedObjectBounds.length > 50) {
          this.updateCameraOpsGPU().catch(() => {});
        }
      } else {
        // 🚀 CPU FALLBACK: Headlight'ları güncelle - ışık ve gölgeler için gerekli!
        for (let i = 0; i < headlightCount; i++) {
          const light = this.cachedHeadlights[i] as any;
          // 🚀 PERFORMANS: Invisible ışıkları atla
          if (!light.visible) continue;
          if (light.kameraTakipAktif && light.updateLightPosition) {
            light.updateLightPosition();
          }
        }
      }

      // 🚀🚀🚀 PERFORMANS: During interaction, render with MINIMAL overhead
      // Disable sorting and shadows for maximum speed
      const prevSortObjects = this.sunucu.sortObjects;
      // 🔧 WebGPU uyumlu: shadowMap sadece WebGL'de mevcut
      const hasShadowMap = this.sunucu.shadowMap && 'autoUpdate' in this.sunucu.shadowMap;
      const prevShadowAutoUpdate = hasShadowMap ? this.sunucu.shadowMap.autoUpdate : false;
      this.sunucu.sortObjects = false;  // 🚀 Skip depth sorting
      if (hasShadowMap) {
        this.sunucu.shadowMap.autoUpdate = false;  // 🚀 Skip shadow updates
      }

      const len = this.sahneler.length;
      for (let i = 0; i < len; i++) {
        const sahne = this.sahneler[i];
        if (sahne.visible) {
          this.sunucu.render(sahne, this.aktifKamera);
        }
      }

      // Restore settings
      this.sunucu.sortObjects = prevSortObjects;
      if (hasShadowMap) {
        this.sunucu.shadowMap.autoUpdate = prevShadowAutoUpdate;
      }

      // 🔥 FIX: FikoEksen objelerini en üstte render et (overlay pass)
      this.renderEksenOverlay();

      if (this.css3dSahne) {
        this.sunucuCSS3d.render(this.css3dSahne, this.aktifKamera);
      }
      // CSS2D Render - SmartPoint için
      if (this.sunucuCSS2d && this.sahneler?.[0]) {
        this.sunucuCSS2d.render(this.sahneler[0], this.aktifKamera);
      }
      // 🎯 FIX: TWEEN animasyonlarını interaction sırasında da güncelle (ekranaSigdir çalışsın)
      if (typeof TWEEN !== 'undefined') {
        TWEEN.update(performance.now());
      }
      if (_arKameraSwap) (this as any).aktifKamera = _orthoKameraRef;
      this.renderComplete.emit();
      return;
    }

    const fc = this.frameCount;
    const fc1 = (fc & 1) === 0;
    const fc3 = (fc & 3) === 0;
    const fc7 = (fc & 7) === 0;
    const fc15 = (fc & 15) === 0;
    const fc63 = (fc & 63) === 0;

    const headlightCount = this.cachedHeadlights.length;
    if (headlightCount > 0) {
      for (let i = 0; i < headlightCount; i++) {
        const light = this.cachedHeadlights[i] as any;
        if (!light.visible) continue;
        if (light.kameraTakipAktif && light.updateLightPosition) {
          light.updateLightPosition();
        }
      }
    } else if (this.sahneler.length > 0 && fc63) {
      for (let s = 0; s < this.sahneler.length; s++) {
        this.traverseVisibleOnly(this.sahneler[s], obj => {
          if (obj.type === 'FikoIsikYonsel') {
            this.cachedHeadlights.push(obj as THREE.Light);
            const light = obj as any;
            if (light.kameraTakipAktif && light.updateLightPosition) {
              light.updateLightPosition();
            }
          }
        });
      }
    }

    // 🚀 PERFORMANS: Dynamic Resolution Scaler - Only for low FPS targets (30-45 FPS)
    if (this.dynamicResolutionScaler && this.maxFPS <= 45) {
      this.dynamicResolutionScaler.updateFrameTiming(this.lastFrameTime || 16.67);
    }

    // 🚀 PERFORMANS: TAA - Apply only when camera is stable
    if (this.temporalAA && fc1) {
      this.temporalAA.applyJitter();
    }

    // 🚀 PERFORMANS: Texture Streaming - Low frequency update (every 16 frames)
    if (this.textureStreaming && fc15) {
      this.textureStreaming.updateStreaming(this.aktifKamera);
    }

    // 🚀 PERFORMANS: Virtual Textures - Low frequency update (every 8 frames)
    if (this.virtualTextures && this.aktifKamera && fc7) {
      this.virtualTextures.streamTextures(this.aktifKamera, this.sahneler[0] || new THREE.Scene());
    }

    // Shadow Optimizer - KALDIRILDI (modül mevcut değil)

    // 🚀 PERFORMANS: GPU Physics - Only if explicitly enabled (every 2 frames)
    if (this.gpuPhysicsCompute && fc1) {
      this.gpuPhysicsCompute.simulateStep((this.lastFrameTime || 16.67) / 1000);
    }

    // 🚀 PERFORMANS: Occlusion Culling - every 16 frames (daha az CPU yükü)
    if (this.occlusionCulling && fc15) {
      this.occlusionCulling.updateVisibility();
    }

    // 🚀 RENDER PERFORMANS: Streaming LOD system (büyük CAD modelleri için)
    // Memory budgeting + progressive loading
    const shouldUpdateLOD = this.isInteracting || fc15;
    if (this.streamingLODManager && shouldUpdateLOD) {
      const deltaTime = (this.lastFrameTime || 16.67) / 1000; // Convert ms to seconds
      this.streamingLODManager.update(deltaTime);
    }

    // 🚀 RENDER PERFORMANS: Advanced LOD system
    // İnteraksiyon: Her frame (agresif LOD için)
    // Durağan: Her 16 frame (CPU yükü azaltma)
    if (this.advancedLOD && this.aktifKamera && shouldUpdateLOD) {
      this.advancedLOD.updateLODs(this.aktifKamera);
    }

    // 🚀 RENDER PERFORMANS: Frustum Culling - Her frame (interaksiyon) veya her 16 frame (durağan)
    // İnteraksiyon sırasında kamera sürekli hareket ettiği için her frame gerekli
    const shouldUpdateCulling = this.isInteracting || fc15;
    if (this.enableFrustumCulling && this.aktifKamera && shouldUpdateCulling) {
      this.frustumCuller.updateFrustum(this.aktifKamera);
    }

    // 🚀 RENDER PERFORMANS: LOD Manager - Fallback (Advanced LOD yoksa)
    // İnteraksiyon: Her frame, Durağan: Her 16 frame
    if (this.enableLOD && !this.advancedLOD && this.aktifKamera && shouldUpdateLOD) {
      this.lodManager.updateLODs(this.aktifKamera);
    }

    // 🚀 PERFORMANS: Main scene rendering - unrolled for common case (1-2 scenes)
    const sceneCount = this.sahneler.length;
    if (sceneCount === 1) {
      // Single scene fast path
      if (this.sahneler[0].visible) {
        this.sunucu.render(this.sahneler[0], this.aktifKamera);
      }
    } else if (sceneCount === 2) {
      // Two scenes fast path
      if (this.sahneler[0].visible) this.sunucu.render(this.sahneler[0], this.aktifKamera);
      if (this.sahneler[1].visible) this.sunucu.render(this.sahneler[1], this.aktifKamera);
    } else {
      // General case
      for (let i = 0; i < sceneCount; i++) {
        if (this.sahneler[i].visible) {
          this.sunucu.render(this.sahneler[i], this.aktifKamera);
        }
      }
    }

    // 🔥 FIX: FikoEksen objelerini en üstte render et (overlay pass)
    this.renderEksenOverlay();

    // 🚀 GPU HIGHLIGHT: GPU-accelerated highlight render (ana render'dan SONRA)
    if (this.sahneler[0]?.secimler) {
      this.sahneler[0].secimler.renderGPUHighlights();
    }

    // 🚀 PERFORMANS: CSS3D Render - Every frame for labels
    if (this.css3dSahne) {
      this.sunucuCSS3d.render(this.css3dSahne, this.aktifKamera);
    }

    // 🚀 CSS2D Render - FikoSmartPoint label/tooltip için
    if (this.sunucuCSS2d && this.sahneler?.[0]) {
      this.sunucuCSS2d.render(this.sahneler[0], this.aktifKamera);
    }

    // 🚀 RENDER PERFORMANS: Post-processing - Skip during interaction for speed
    // TAA, SSAO, Bloom gibi ağır efektler sadece durağan iken
    if (this.enablePostProcessing && !this.isInteracting) {
      this.postProcessing.render();
    }

    // 🎯 FIX: TWEEN update - Her zaman çağır (getAll() bazı versiyonlarda güvenilir değil)
    if (typeof TWEEN !== 'undefined') {
      TWEEN.update(performance.now());
    }

    // 🚀 PERFORMANS: Performance monitoring - every 64 frames (~1 time/sec at 60fps)
    if (fc63) {
      const perf = this.performanceMonitor.update();

      // 🚀 PERFORMANS: GPU Memory check - async to avoid sync stalls
      if (this.gpuMemoryManager) {
        const now = performance.now();
        if (now - this.lastGPUMemoryCheck > this.gpuMemoryCheckInterval) {
          // Fire-and-forget async check
          queueMicrotask(() => {
            const memReport = this.gpuMemoryManager?.getMemoryReport();
            // GPU Memory check - status: memReport?.status
          });
          this.lastGPUMemoryCheck = now;
        }
      }

      this.performanceUpdate.emit(perf);
    }

    // 🚀 PERFORMANS: Use bitwise AND for frame counter wrap (64 frames = 0x3F)
    this.frameCount = (fc + 1) & 63;
    if (_arKameraSwap) (this as any).aktifKamera = _orthoKameraRef;
    this.renderComplete.emit();
  }

  // Tek Kare Çizimi (immediate render - GPU işlemleri için)
  // 🚀 PERFORMANS: Deferred render için markNeedsRender() kullanın
  ciz(): void {
    if (this.animationFrameId || this.sahneler.length === 0 || this._isDestroyed) return;

    this.ngZone.runOutsideAngular(() => {
      this.sun();
      this.needsRender = false;
    });
  }

  // ========================================================================
  // AR MODU - Kamera arka plan + anchor sistemi
  // ========================================================================

  private _arOncekiBackground: any = undefined;

  arModuAc(arModu: any): void {
    this._arModu = arModu;
    for (const sahne of this.sahneler) {
      if (this._arOncekiBackground === undefined) {
        this._arOncekiBackground = sahne.background;
      }
      sahne.background = null;
    }
    this.markNeedsRender();
  }

  arModuKapat(): void {
    this._arModu = null;
    if (this._arOncekiBackground !== undefined) {
      for (const sahne of this.sahneler) {
        sahne.background = this._arOncekiBackground;
      }
      this._arOncekiBackground = undefined;
    }
    this.markNeedsRender();
  }

  // ========================================================================
  // 🚀 GPU COMPUTE OPTIMIZED - Deformasyon, Particle, Skinning API
  // ========================================================================

  /**
   * Mesh deformasyonu uygula (GPU hızlandırmalı)
   * @param mesh Deformasyon uygulanacak mesh
   * @param type Deformasyon tipi: 'bend' | 'twist' | 'scale' | 'wave'
   * @param intensity Deformasyon şiddeti (0-1 arası önerilir)
   * @param time Animasyon zamanı (animasyonlu efektler için)
   */
  gpuDeformMesh(
    mesh: THREE.Mesh,
    type: 'bend' | 'twist' | 'scale' | 'wave',
    intensity: number,
    time: number = 0
  ): void {
    if (!this.gpuComputeOptimized || !mesh.geometry) return;

    const deformed = this.gpuComputeOptimized.computeMeshDeformation(
      mesh.geometry as THREE.BufferGeometry,
      type,
      intensity,
      time
    );

    // Deformed pozisyonları uygula
    const posAttr = mesh.geometry.getAttribute('position');
    if (posAttr) {
      (posAttr.array as Float32Array).set(deformed);
      posAttr.needsUpdate = true;
      mesh.geometry.computeVertexNormals();
    }

    this.ciz();
  }

  /**
   * GPU Particle sistemi oluştur
   * @param particleCount Parçacık sayısı (binlerce olabilir)
   * @param maxVelocity Maksimum hız vektörü
   * @returns THREE.Points objesi
   */
  gpuCreateParticleSystem(
    particleCount: number = 10000,
    maxVelocity: THREE.Vector3 = new THREE.Vector3(10, 10, 10)
  ): THREE.Points | null {
    if (!this.gpuComputeOptimized) return null;

    const particles = this.gpuComputeOptimized.createGPUParticleSystem(
      particleCount,
      maxVelocity
    );

    // Sahneye ekle
    if (this.sahneler.length > 0) {
      this.sahneler[0].add(particles);
      FikoThreeJsComponent.debugLog(`🎆 GPU Particle System oluşturuldu: ${particleCount} parçacık`);
    }

    return particles;
  }

  /**
   * Particle sistemini güncelle (her frame çağır)
   * @param particles Particle sistemi
   * @param deltaTime Frame süresi (saniye)
   * @param gravity Yerçekimi vektörü
   */
  gpuUpdateParticles(
    particles: THREE.Points,
    deltaTime: number,
    gravity: THREE.Vector3 = new THREE.Vector3(0, -9.81, 0)
  ): void {
    if (!this.gpuComputeOptimized) return;

    this.gpuComputeOptimized.updateParticleSystem(particles, deltaTime, gravity);
    this.ciz();
  }

  /**
   * Instanced mesh oluştur (binlerce obje tek draw call'da)
   * @param geometry Kaynak geometri
   * @param material Materyal
   * @param count Instance sayısı
   * @param positions Pozisyonlar (Float32Array, her instance için 3 değer)
   * @param rotations Rotasyonlar (Float32Array, her instance için 4 değer - quaternion)
   * @param scales Ölçekler (Float32Array, her instance için 3 değer)
   */
  gpuCreateInstancedMesh(
    geometry: THREE.BufferGeometry,
    material: THREE.Material,
    count: number,
    positions: Float32Array,
    rotations: Float32Array,
    scales: Float32Array
  ): THREE.InstancedMesh | null {
    if (!this.gpuComputeOptimized) return null;

    const instancedMesh = this.gpuComputeOptimized.createInstancedMeshCompute(
      geometry,
      material,
      count,
      positions,
      rotations,
      scales
    );

    // Sahneye ekle
    if (this.sahneler.length > 0) {
      this.sahneler[0].add(instancedMesh);
      FikoThreeJsComponent.debugLog(`🔷 Instanced Mesh oluşturuldu: ${count} instance`);
    }

    return instancedMesh;
  }

  /**
   * GPU Compute Optimized instance'ına erişim
   */
  getGPUComputeOptimized(): any {
    return this.gpuComputeOptimized;
  }

  // Sürekli Çizim (FPS Sınırlı)
  surekliCiz(): void {
    if (this.animationFrameId) return;

    this.renderMode = 'continuous';
    this.lastFrameTime = performance.now();

    const renderLoop = (currentTime: number) => {
      const elapsed = currentTime - this.lastFrameTime;

      if (elapsed >= this.frameInterval) {
        this.lastFrameTime = currentTime - (elapsed % this.frameInterval);
        this.sun();
      }

      this.animationFrameId = requestAnimationFrame(renderLoop);
    };

    this.ngZone.runOutsideAngular(() => {
      this.animationFrameId = requestAnimationFrame(renderLoop);
    });
  }

  // Sürekli Çizimi Durdur
  surekliCizIptal(): void {
    this.renderMode = 'on-demand';
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = undefined;
    }

    this.sunucu.setAnimationLoop(null);

    // ❌ TWEEN.removeAll() KALDIRILDI
    // Aktif TWEEN animasyonlarını iptal etmeyin!
    // Promise.all ile kontrollü şekilde bitmelerini bekleyin
    // if (typeof TWEEN !== 'undefined') {
    //   TWEEN.removeAll();
    // }

    this.renderMode = 'on-demand';
  }

  // Gelişmiş Özellikler

  // PERFORMANS: Texture loading with LRU cache eviction
  loadTexture(url: string): THREE.Texture {
    if (this.textureCache.has(url)) {
      // PERFORMANS: Update LRU access order
      const index = this.textureCacheAccessOrder.indexOf(url);
      if (index > -1) {
        this.textureCacheAccessOrder.splice(index, 1);
      }
      this.textureCacheAccessOrder.push(url);
      return this.textureCache.get(url)!;
    }

    const loader = new THREE.TextureLoader();
    const texture = loader.load(url, () => this.markNeedsRender());

    // PERFORMANS: Estimate texture memory (rough estimate: w * h * 4 bytes for RGBA)
    const estimatedMemory = ((texture.image as any)?.width || 0) * ((texture.image as any)?.height || 0) * 4 / (1024 * 1024);
    this.estimatedTextureCacheMemory += estimatedMemory;

    // PERFORMANS: Evict LRU textures if cache is full
    while (this.textureCache.size >= this.MAX_TEXTURE_CACHE_SIZE ||
           this.estimatedTextureCacheMemory > this.MAX_TEXTURE_MEMORY_MB) {
      const lruKey = this.textureCacheAccessOrder.shift();
      if (lruKey && this.textureCache.has(lruKey)) {
        const evictedTexture = this.textureCache.get(lruKey)!;
        evictedTexture.dispose();
        this.textureCache.delete(lruKey);
        const evictedMemory = ((evictedTexture.image as any)?.width || 0) * ((evictedTexture.image as any)?.height || 0) * 4 / (1024 * 1024);
        this.estimatedTextureCacheMemory -= evictedMemory;
      }
    }

    this.textureCache.set(url, texture);
    this.textureCacheAccessOrder.push(url);
    return texture;
  }

  // PERFORMANS: Material caching with size limit
  getMaterial(id: string, factory: () => THREE.Material): THREE.Material {
    if (this.materialCache.has(id)) {
      const mat = this.materialCache.get(id)!;
      this.materialCache.delete(id);
      this.materialCache.set(id, mat);
      return mat;
    }

    // PERFORMANS: Evict oldest materials if cache is full
    if (this.materialCache.size >= this.MAX_MATERIAL_CACHE_SIZE) {
      const firstKey = this.materialCache.keys().next().value;
      if (firstKey) {
        const oldMat = this.materialCache.get(firstKey);
        oldMat?.dispose();
        this.materialCache.delete(firstKey);
      }
    }

    const material = factory();
    this.materialCache.set(id, material);
    return material;
  }

  // PERFORMANS: Geometry caching with size limit
  getGeometry(id: string, factory: () => THREE.BufferGeometry): THREE.BufferGeometry {
    if (this.geometryCache.has(id)) {
      const geo = this.geometryCache.get(id)!;
      this.geometryCache.delete(id);
      this.geometryCache.set(id, geo);
      return geo;
    }

    // PERFORMANS: Evict oldest geometries if cache is full
    if (this.geometryCache.size >= this.MAX_GEOMETRY_CACHE_SIZE) {
      const firstKey = this.geometryCache.keys().next().value;
      if (firstKey) {
        const oldGeo = this.geometryCache.get(firstKey);
        oldGeo?.dispose();
        this.geometryCache.delete(firstKey);
      }
    }

    const geometry = factory();
    this.geometryCache.set(id, geometry);
    return geometry;
  }

  // Instanced Mesh Oluşturma
  createInstancedMesh(
    geometry: THREE.BufferGeometry,
    material: THREE.Material,
    count: number,
    id: string
  ): THREE.InstancedMesh {
    return this.sceneOptimizer.createInstancedMesh(geometry, material, count, id);
  }

  // LOD Oluşturma
  createLOD(id: string, meshes: THREE.Mesh[], distances: number[]): THREE.LOD {
    const lod = this.lodManager.createLOD(id, meshes, distances);
    this.markNeedsRender();
    return lod;
  }

  // Sahne İstatistikleri
  getSceneStats(): {
    objects: number;
    triangles: number;
    textures: number;
    materials: number;
    drawCalls: number;
  } {
    let objects = 0;
    let triangles = 0;

    this.sahneler.forEach(sahne => {
      sahne.traverse((obj: THREE.Object3D) => {
        objects++;
        if (obj instanceof THREE.Mesh && obj.geometry) {
          const geo = obj.geometry as THREE.BufferGeometry;
          const index = geo.index;
          const pos = geo.attributes.position;
          
          if (index) {
            triangles += index.count / 3;
          } else if (pos) {
            triangles += pos.count / 3;
          }
        }
      });
    });

    return {
      objects,
      triangles: Math.floor(triangles),
      textures: this.textureCache.size,
      materials: this.materialCache.size,
      drawCalls: this.sunucu.info.render.calls
    };
  }

  // Screenshot Alma
  takeScreenshot(filename = 'screenshot.webp'): void {
    this.sun(); // Render et
    this.sunucu.domElement.toBlob((blob) => {
      if (blob) {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        URL.revokeObjectURL(url);
      }
    });
  }

  // Render Modunu Değiştir
  setRenderMode(mode: 'continuous' | 'on-demand'): void {
    if (mode === 'continuous') {
      this.surekliCiz();
    } else {
      this.surekliCizIptal();
    }
  }

  /**
   * FikoThreeJS ayarları diyaloğunu aç
   */
  acAyarlarDialogu(): void {
    this.DialogFiko.ac({
      bilesen: FikoThreeJsAyarlarComponent,
      ayarlar: {
        baslik: 'Fiko3D Ayarları',
        simgeResim: './simgeler/proje.webp'
      },
      parametreler: {
        ayarlarDegistiCallback: (ayarlar: any) => this.ayarlariUygula(ayarlar)
      }
    });
  }

  /**
   * Real-Time Collaboration diyaloğunu aç
   */
  acCollabDialogu(): void {
    const dialogId = 'fiko-collab-dialog';

    // Mevcut dialog varsa öne getir
    const mevcutDialog = this.DialogFiko.idIleAl(dialogId);
    if (mevcutDialog && mevcutDialog.miAcik) {
      const dialogEl = document.getElementById(mevcutDialog.id_dialog);
      if (dialogEl) {
        const yeniZIndex = this.DialogFiko.overlayZIndexAl?.() || 99999;
        dialogEl.style.setProperty('z-index', yeniZIndex.toString(), 'important');
        const parent = dialogEl.parentElement;
        if (parent) {
          parent.style.setProperty('z-index', yeniZIndex.toString(), 'important');
        }
        this.DialogFiko.dialogTiklandi?.(mevcutDialog);
        dialogEl.focus();
      }
      return;
    }

    this.DialogFiko.ac({
      bilesen: FikoCollabDialogComponent,
      id: dialogId,
      ayarlar: {
        baslik: 'Isbirligi',
        simgeResim: './simgeler/kullanici-gruplari.webp',
        modalMi: false,
        sinirlandi: false,
        boyut: { w: 280, h: 'auto' }
      },
      parametreler: {
        scene: this.sahneler?.[0],
        camera: this.aktifKamera,
        orbitControls: this.aktifKontrol
      }
    });
  }

  /**
   * Setup collaboration event handlers - cursor and selection sync
   */
  private setupCollabEventHandlers(): void {
    console.log('[FikoThreeJS] Setting up collab event handlers');

    // Ensure orbit controls reference is set for camera sync
    if (this.aktifKontrol) {
      this.collabService.setOrbitControls(this.aktifKontrol);
    }

    // Enable collab mode on secililer
    if (this.secililer) {
      this.secililer.collabEnabled = true;
      console.log('[FikoThreeJS] Collab enabled on secililer');

      // Subscribe to selection changes
      this.collabSelectionSub = this.secililer.collabSelectionChanged.subscribe(
        (data: { uuids: string[]; mode: 'replace' | 'add' | 'remove' }) => {
          this.collabService.emitSelectionChanged(data.uuids, data.mode);
        }
      );

      // Subscribe to hover/cursor events
      this.collabCursorSub = this.secililer.uzerinde.subscribe(
        (intersection: THREE.Intersection | null) => {
          // Emit cursor with raycaster ray
          if (this.collabRaycaster && this.aktifKamera) {
            this.collabService.emitCursorMove(
              this.collabRaycaster.ray,
              intersection || undefined
            );
          }
        }
      );
    }

    // Add mouse move listener for cursor tracking
    this.setupCollabCursorTracking();

    // Setup camera follow callback
    this.setupCollabCameraFollow();

    // Setup camera change emission
    this.setupCollabCameraEmit();

    // Setup scene object sync callbacks
    this.setupCollabSceneSync();
  }

  /**
   * Setup mouse tracking for collaboration cursor
   */
  private setupCollabCursorTracking(): void {
    const canvas = this.canvas?.nativeElement;
    if (!canvas) return;

    const onMouseMove = (event: MouseEvent) => {
      if (!this.collabService.currentRoomId()) return;

      const rect = canvas.getBoundingClientRect();
      this.collabMouseNDC.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      this.collabMouseNDC.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      // Update raycaster
      this.collabRaycaster.setFromCamera(this.collabMouseNDC, this.aktifKamera);

      // Emit cursor position (throttled in service)
      this.collabService.emitCursorMove(this.collabRaycaster.ray);
    };

    canvas.addEventListener('mousemove', onMouseMove);
    (this as any)._collabMouseMoveHandler = onMouseMove;
  }

  /**
   * Setup camera follow - apply followed user's camera position
   */
  private _isApplyingFollowCamera = false;

  private setupCollabCameraFollow(): void {
    this.collabService.onCameraFollowUpdate = (data) => {
      if (!this.aktifKontrol || !this.aktifKamera) return;

      // Set flag to prevent feedback loop
      this._isApplyingFollowCamera = true;

      // Smoothly interpolate to the followed user's camera
      const targetPos = new THREE.Vector3(data.position[0], data.position[1], data.position[2]);
      const targetLookAt = new THREE.Vector3(data.target[0], data.target[1], data.target[2]);

      // Lerp camera position
      this.aktifKamera.position.lerp(targetPos, 0.15);

      // Update orbit controls target
      if (this.aktifKontrol.target) {
        this.aktifKontrol.target.lerp(targetLookAt, 0.15);
      }

      // Apply zoom
      if (data.zoom != null && data.zoom > 0) {
        if ((this.aktifKamera as any).isPerspectiveCamera) {
          // For PerspectiveCamera: zoom is distance-to-target
          // We adjust camera position along the direction to match the zoom distance
          const currentDir = new THREE.Vector3().subVectors(
            this.aktifKamera.position, this.aktifKontrol.target
          ).normalize();
          const currentDist = this.aktifKamera.position.distanceTo(this.aktifKontrol.target);
          const targetDist = data.zoom;
          const newDist = currentDist + (targetDist - currentDist) * 0.15;
          this.aktifKamera.position.copy(this.aktifKontrol.target).addScaledVector(currentDir, newDist);
        } else if ((this.aktifKamera as any).isOrthographicCamera) {
          // For OrthographicCamera: apply zoom directly
          const cam = this.aktifKamera as THREE.OrthographicCamera;
          cam.zoom += (data.zoom - cam.zoom) * 0.15;
          cam.updateProjectionMatrix();
        }
      }

      this.aktifKontrol.update?.();

      // Clear flag after update
      this._isApplyingFollowCamera = false;

      // Trigger render
      this.ciz();
    };
  }

  /**
   * Setup camera emission - send camera updates when moving
   */
  private setupCollabCameraEmit(): void {
    if (!this.aktifKontrol) return;

    // Emit camera position on orbit control change
    const onCameraChange = () => {
      if (!this.collabService.currentRoomId()) return;

      // Skip emission if this change was caused by follow (prevents feedback loop)
      if (this._isApplyingFollowCamera) return;

      // Always emit camera updates so other users can see/follow us
      // The throttle in emitCameraUpdate prevents flooding
      this.collabService.emitCameraUpdate();
      this.collabService.emitAvatarUpdate();
    };

    this.aktifKontrol.addEventListener('change', onCameraChange);
    (this as any)._collabCameraChangeHandler = onCameraChange;
  }

  /**
   * Setup scene object sync - receive remote object changes
   */
  private setupCollabSceneSync(): void {
    // Transform: position, rotation, scale
    this.collabService.onSceneObjectTransform = (data) => {
      if (!this.sahneler?.[0]) return;
      const obj = this.findObjectByName(data.objectName);
      if (!obj) return;

      console.log('[FikoThreeJS] Remote transform:', data.objectName);
      if (data.position) obj.position.set(data.position[0], data.position[1], data.position[2]);
      if (data.rotation) obj.rotation.set(data.rotation[0], data.rotation[1], data.rotation[2]);
      if (data.scale) obj.scale.set(data.scale[0], data.scale[1], data.scale[2]);
      this.ciz();
    };

    // Visibility
    this.collabService.onSceneObjectVisibility = (data) => {
      if (!this.sahneler?.[0]) return;
      const obj = this.findObjectByName(data.objectName);
      if (!obj) return;

      console.log('[FikoThreeJS] Remote visibility:', data.objectName, data.visible);
      obj.visible = data.visible;
      this.ciz();
    };

    // Color
    this.collabService.onSceneObjectColor = (data) => {
      if (!this.sahneler?.[0]) return;
      const obj = this.findObjectByName(data.objectName);
      if (!obj) return;

      console.log('[FikoThreeJS] Remote color:', data.objectName, data.color);
      obj.traverse((child: any) => {
        if (child.isMesh && child.material) {
          const mat = child.material;
          if (mat.color) mat.color.set(data.color);
          if (data.opacity !== undefined) {
            mat.opacity = data.opacity;
            mat.transparent = data.opacity < 1;
          }
          mat.needsUpdate = true;
        }
      });
      this.ciz();
    };

    // Delete
    this.collabService.onSceneObjectDelete = (data) => {
      if (!this.sahneler?.[0]) return;
      const obj = this.findObjectByName(data.objectName);
      if (!obj) return;

      console.log('[FikoThreeJS] Remote delete:', data.objectName);
      obj.parent?.remove(obj);
      // Dispose geometry and materials
      obj.traverse((child: any) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach((m: any) => m.dispose());
          } else {
            child.material.dispose();
          }
        }
      });
      this.ciz();
    };

    // File share - remote user uploaded a file
    this.collabService.onFileShareReady = (data) => {
      console.log('[FikoThreeJS] File share ready:', data.fileName, 'from:', data.userName);
      // Auto-download and import the shared file
      this.handleRemoteFileShare(data);
    };
  }

  /**
   * Find object in scene by name
   */
  private findObjectByName(name: string): THREE.Object3D | null {
    let result: THREE.Object3D | null = null;
    this.sahneler?.[0]?.traverse((obj: any) => {
      if (obj.name === name && !result) {
        result = obj;
      }
    });
    return result;
  }

  /**
   * Handle remote file share - download and import
   */
  /**
   * Callback for external file import handling.
   * Parent component should set this to handle STEP file imports.
   */
  public onCollabFileImport: ((file: File, fileName: string, fromUser: string) => void) | null = null;

  private async handleRemoteFileShare(data: any): Promise<void> {
    try {
      console.log('[FikoThreeJS] Downloading shared file:', data.fileUrl);
      const response = await fetch(data.fileUrl);
      if (!response.ok) throw new Error('Download failed: ' + response.status);

      const blob = await response.blob();
      const file = new File([blob], data.fileName, { type: blob.type });

      console.log('[FikoThreeJS] Shared file downloaded:', data.fileName, 'size:', file.size);

      // Delegate to external handler (parent component handles the actual import)
      if (this.onCollabFileImport) {
        this.onCollabFileImport(file, data.fileName, data.userName);
      } else {
        console.warn('[FikoThreeJS] No onCollabFileImport handler set - file not imported');
      }
    } catch (err) {
      console.error('[FikoThreeJS] Failed to download shared file:', err);
    }
  }

  /**
   * Cleanup collaboration event handlers
   */
  private cleanupCollabEventHandlers(): void {
    if (this.secililer) {
      this.secililer.collabEnabled = false;
    }

    this.collabSelectionSub?.unsubscribe();
    this.collabCursorSub?.unsubscribe();
    this.collabSelectionSub = null;
    this.collabCursorSub = null;

    // Remove mouse listener
    const canvas = this.canvas?.nativeElement;
    if (canvas && (this as any)._collabMouseMoveHandler) {
      canvas.removeEventListener('mousemove', (this as any)._collabMouseMoveHandler);
      (this as any)._collabMouseMoveHandler = null;
    }

    // Remove camera change listener
    if (this.aktifKontrol && (this as any)._collabCameraChangeHandler) {
      this.aktifKontrol.removeEventListener('change', (this as any)._collabCameraChangeHandler);
      (this as any)._collabCameraChangeHandler = null;
    }

    // Clear camera follow callback
    this.collabService.onCameraFollowUpdate = null;

    // Clear scene sync callbacks
    this.collabService.onSceneObjectTransform = null;
    this.collabService.onSceneObjectVisibility = null;
    this.collabService.onSceneObjectColor = null;
    this.collabService.onSceneObjectDelete = null;
    this.collabService.onFileShareReady = null;
  }

  /**
   * 3D Line Dialogunu Aç
   */
  ac3DLineDialogu(): void {
    const dialogId = 'fiko-3d-line-dialog-create';

    // Mevcut dialog varsa öne getir
    const mevcutDialog = this.DialogFiko.idIleAl(dialogId);
    if (mevcutDialog && mevcutDialog.miAcik) {
      // Dialog zaten açık, sayfadaki en yüksek z-index'i bulup öne getir
      const dialogEl = document.getElementById(mevcutDialog.id_dialog);
      if (dialogEl) {
        // overlayZIndexAl: Sayfadaki TÜM elementleri tarar
        const yeniZIndex = this.DialogFiko.overlayZIndexAl?.() || 99999;
        dialogEl.style.setProperty('z-index', yeniZIndex.toString(), 'important');
        const parent = dialogEl.parentElement;
        if (parent) {
          parent.style.setProperty('z-index', yeniZIndex.toString(), 'important');
        }
        this.DialogFiko.dialogTiklandi?.(mevcutDialog);
        dialogEl.focus();
      }
      return;
    }

    this.DialogFiko.ac({
      bilesen: FikoYT3dLineDialogComponent,
      id: dialogId,
      ayarlar: {
        baslik: '3D Çizgi Oluştur',
        modalMi: false,
        sinirlandi: false,
        boyut: { w: 520, h: 'auto' }
      },
      parametreler: {
        sahne: this.sahneler?.[0],
        cizgiOlusturulduCallback: (cizgi: FikoYT3dLine) => {
          const sahne = this.sahneler?.[0];
          if (sahne && cizgi) {
            sahne.add(cizgi);
            this.ciz();
            this.FikoYT?.degisti?.emit();
          }
        }
      }
    });
  }

  /**
   * Three.js ayarlarını uygula
   */
  private ayarlariUygula(ayarlar: any): void {
    // Önceki ayarları kopyala
    this.threejsAyarlar = { ...ayarlar };

    // 🚀 SÜREKLİ ÇİZİM MODU - Ayarlardan kontrol et
    if (ayarlar.surekliCizimModu !== undefined) {
      if (ayarlar.surekliCizimModu) {
        this.surekliCiz();
        FikoThreeJsComponent.debugLog('✅ Sürekli Çizim Modu AKTİF');
      } else {
        this.surekliCizIptal();
        FikoThreeJsComponent.debugLog('🛑 Sürekli Çizim Modu KAPALI');
      }
    }

    // 1. Render modunu uygula
    if (ayarlar.renderModu && ayarlar.renderModu !== this.renderMode) {
      this.setRenderMode(ayarlar.renderModu);
    }

    // 2. MaxFPS'i uygula
    if (ayarlar.maxFPS) {
      this.maxFPS = ayarlar.maxFPS;
      this.frameInterval = 1000 / this.maxFPS;
    }

    // 3. Frustum culling
    if (ayarlar.frustumCullingAktif !== undefined) {
      this.enableFrustumCulling = ayarlar.frustumCullingAktif;
    }

    // 4. LOD sistemi
    if (ayarlar.lodSistemiAktif !== undefined) {
      this.enableLOD = ayarlar.lodSistemiAktif;
    }

    // 5. Anti-alias
    if (ayarlar.antialiasAktif !== undefined) {
      this.antiAlias = ayarlar.antialiasAktif;
    }

    // 6. Pixel ratio
    if (ayarlar.pixelRatio && this.sunucu) {
      this.pixelRatio = ayarlar.pixelRatio;
      this.sunucu.setPixelRatio(Math.min(this.pixelRatio, 1.5));
    }

    // ===== GPU OPTIMIZATION (TOP 5) =====
    // Dynamic Resolution - Dinamik resolüsyon ayarlama
    // NOTE: Only useful on lower-end devices or when targeting lower FPS (30/45)
    if (ayarlar.dynamicResolutionAktif && !this.dynamicResolutionScaler && this.sunucu) {
      // Initialize on demand
      try {
        this.dynamicResolutionScaler = new FikoDynamicResolutionScaler(this.sunucu);
        this.dynamicResolutionScaler.setTargetFPS(this.maxFPS);
        FikoThreeJsComponent.debugLog('Dynamic Resolution Scaler initialized (target:', this.maxFPS + 'FPS)');
      } catch (e) {
        this.dynamicResolutionScaler = null;
      }
    } else if (!ayarlar.dynamicResolutionAktif && this.dynamicResolutionScaler) {
      // Cleanup when disabled - restore to full resolution
      this.dynamicResolutionScaler.dispose();
      this.dynamicResolutionScaler = null;
      FikoThreeJsComponent.debugLog('🛑 Dynamic Resolution Scaler disabled');
    }

    if (this.dynamicResolutionScaler && ayarlar.dynamicResolutionAktif) {
      this.dynamicResolutionScaler.setTargetFPS(this.maxFPS);
    }

    // GPU Texture Filtering - Doku filtreleme (has setEnabled)
    if (this.gpuTextureFiltering && ayarlar.gpuTextureFilteringAktif !== undefined) {
      this.gpuTextureFiltering.setEnabled(ayarlar.gpuTextureFilteringAktif);
    }

    // ===== GPU COMPUTE EXTREME (10 sistem) =====
    // GPU Physics Compute - DEVRE DIŞI (modül mevcut değil)
    // GPU Frustum Culling - DEVRE DIŞI (modül mevcut değil)
    // GPU Occlusion Queries - DEVRE DIŞI (modül mevcut değil)
    // GPU Transform Feedback - DEVRE DIŞI (modül mevcut değil)
    // GPU Skinning - DEVRE DIŞI (modül mevcut değil)
    // GPU Particle Advection - DEVRE DIŞI (modül mevcut değil)
    // GPU Tile Deferred Rendering - DEVRE DIŞI (modül mevcut değil)
    // GPU Async Compute Pipeline - DEVRE DIŞI (modül mevcut değil)
    // GPU Stream Output - DEVRE DIŞI (modül mevcut değil)

    // ===== ADVANCED OPTIMIZATIONS =====
    // Aggressive LOD - Agresif detay seviyeleri
    if (this.aggressiveLOD && ayarlar.aggressiveLODAktif) {
      if (this.aktifKamera && this.sahneler.length > 0) {
        this.aggressiveLOD.updateLODs(this.sahneler[0], this.aktifKamera);
      }
    }

    // Compute Worker Pool - İşçi havuzu hesaplaması
    if (this.computeWorkerPool && ayarlar.computeWorkerPoolAktif) {
      // Worker pool will be used during render loop for async computations
    }

    // Z-Prepass - Derinlik ön geçişi (has setEnabled)
    if (this.zPrepass && ayarlar.zPrepassAktif !== undefined) {
      this.zPrepass.setEnabled(ayarlar.zPrepassAktif);
    }

    // Frame Budget - Çerçeve bütçesi
    if (this.frameBudget && ayarlar.frameBudgetAktif) {
      // Frame budget will be enforced during render loop
    }

    // Instanced Optimizer - Örneklenmiş mesh optimizasyonu
    if (this.instancedOptimizer && ayarlar.instancedOptimizerAktif) {
      if (this.aktifKamera) {
        this.instancedOptimizer.optimizeVisibility(this.aktifKamera);
      }
    }

    // GPU-Driven Renderer - GPU tarafından yönlendirilmiş render
    if (this.gpuDrivenRenderer && ayarlar.gpuDrivenRendererAktif) {
      if (this.aktifKamera) {
        this.gpuDrivenRenderer.gpuVisibilityOptimization(new Float32Array(), this.aktifKamera);
      }
    }

    // Visibility Precompute - Görünürlük hesaplaması
    if (this.visibilityPrecompute && ayarlar.visibilityPrecomputeAktif) {
      if (this.sahneler.length > 0 && this.aktifKamera) {
        this.visibilityPrecompute.updateDynamicVisibility(this.sahneler[0].children);
      }
    }

    // Aggressive Merging - KALDIRILDI (modül mevcut değil)

    // ===== CRITICAL OPTIMIZATIONS - REINITIALIZE ON TOGGLE =====

    // Temporal AA - Zamansal anti-aliasing
    if (ayarlar.temporalAAaktif) {
      // FikoTemporalAA modülü mevcut değil - devre dışı
      // if (!this.temporalAA) { ... }
    } else {
      // Disable if active
      if (this.temporalAA) {
        this.temporalAA = null;
        FikoThreeJsComponent.debugLog('🛑 TAA disabled');
      }
    }

    // Virtual Textures - Sanal dokular
    if (ayarlar.virtualTexturesAktif) {
      if (!this.virtualTextures) {
        // Initialize if not already done
        this.virtualTextures = new FikoVirtualTextures();
        FikoThreeJsComponent.debugLog('✅ Virtual Textures initialized');
      }
      // Stream textures if camera and scene available
      if (this.aktifKamera && this.sahneler.length > 0) {
        this.virtualTextures.streamTextures(this.aktifKamera, this.sahneler[0]);
      }
    } else {
      // Disable if active
      if (this.virtualTextures) {
        this.virtualTextures = null;
        FikoThreeJsComponent.debugLog('🛑 Virtual Textures disabled');
      }
    }

    // Neural Compression - Sinir ağı sıkıştırması
    // FikoNeuralCompression modülü mevcut değil - devre dışı
    if (ayarlar.neuralCompressionAktif) {
      // Modül mevcut değil
    } else {
      // Disable if active
      if (this.neuralCompression) {
        this.neuralCompression = null;
        FikoThreeJsComponent.debugLog('🛑 Neural Compression disabled');
      }
    }

    // ===== SEÇİM VE HIGHLIGHT AYARLARI =====
    // FikoSecililer class'ına ayarları gönder
    if (this.secililer) {
      try {
        this.secililer.guncelleSecimAyarlari(ayarlar);
      } catch (e) {
        // Error applying selection settings
      }
    }

    // Sahne'deki tüm FikoSecimler sistemlerine ayarları gönder
    if (this.sahneler.length > 0) {
      const scene = this.sahneler[0];
      try {
        // FikoSecimler (legacy) sistemini bul ve güncelle
        const secimler = scene.getObjectByName('Seçimler') as any;
        if (secimler && secimler.guncelleSecimAyarlari) {
          secimler.guncelleSecimAyarlari(ayarlar);
        }

        // FikoSecililer sistemini bul ve güncelle
        const secimlerUzerinde = scene.getObjectByName('SeçimlerÜzerinde') as any;
        if (secimlerUzerinde && secimlerUzerinde.guncelleSecimAyarlari) {
          secimlerUzerinde.guncelleSecimAyarlari(ayarlar);
        }

        // Sahne'de kullanılan diğer seçim sistemlerini ara
        scene.traverse((obj: any) => {
          if (obj.guncelleSecimAyarlari && typeof obj.guncelleSecimAyarlari === 'function') {
            try {
              obj.guncelleSecimAyarlari(ayarlar);
            } catch (e) {
              // Sessiz hata yönetimi
            }
          }
        });
      } catch (e) {
        // Error applying scene selection settings
      }
    }

    // ===== DEBUG AYARLARI =====
    // Debug modunu static property'e ata
    if (ayarlar.debugMode !== undefined) {
      FikoThreeJsComponent.DEBUG_MODE = ayarlar.debugMode;
      if (FikoThreeJsComponent.DEBUG_MODE) {
        FikoThreeJsComponent.debugLog('✅ ThreeJS Debug Mode AKTİF');
      } else {
        FikoThreeJsComponent.debugLog('🛑 ThreeJS Debug Mode PASİF');
      }
    }

    // Scene'de de ayarları sakla (diğer sistemler için)
    if (this.sahneler.length > 0) {
      const scene = this.sahneler[0];
      try {
        // edgeStrength - kenar gücü (default: 10)
        if (ayarlar.kenarGucluklagi !== undefined) {
          (scene as any).edgeStrength = ayarlar.kenarGucluklagi;
        }

        // edgeGlow - kenar yoğunluğu (default: 1.5)
        if (ayarlar.kenarYogunluklagi !== undefined) {
          (scene as any).edgeGlow = ayarlar.kenarYogunluklagi;
        }

        // edgeThickness - kenar kalınlığı (default: 1.0)
        if (ayarlar.kenarKalinligi !== undefined) {
          (scene as any).edgeThickness = ayarlar.kenarKalinligi;
        }

        // Görünür kenar rengi
        if (ayarlar.gorunurKenarRengi) {
          const visibleEdgeColor = new THREE.Color(ayarlar.gorunurKenarRengi);
          (scene as any).visibleEdgeColor = visibleEdgeColor;
        }

        // Gizli kenar rengi
        if (ayarlar.gizliKenarRengi) {
          const hiddenEdgeColor = new THREE.Color(ayarlar.gizliKenarRengi);
          (scene as any).hiddenEdgeColor = hiddenEdgeColor;
        }
      } catch (e) {
        // Error applying edge settings
      }
    }

    // Render'ı işaretle
    this.markNeedsRender();

  }

  /**
   * Menu kapama metodu - change detection döngüsünden sonra çalışması için
   */
  kapaSagMenu(): void {
    if (this.secililer) {
      Promise.resolve().then(() => {
        this.secililer.sagMenuKapat();
      });
    }
  }

  // ============================================================================
  // WebGPU WALL THICKNESS ANALYSIS
  // ============================================================================

  /**
   * WebGPU ve GPU Thickness sistemini başlat
   */
  async initWebGPU(): Promise<boolean> {
    try {
      this.webGPU = FikoWebGPU.getInstance();
      await this.webGPU.init();

      if (this.webGPU.available) {
        // FikoGPUThickness modülü mevcut değil - devre dışı
        // this.gpuThickness = FikoGPUThickness.getInstance();
        // await this.gpuThickness.init();
        return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  }

  /**
   * Seçili mesh için duvar kalınlığı analizi yap
   * GPU hızlandırmalı (20x WASM'dan hızlı)
   */
  async analyzeWallThickness(mesh?: THREE.Mesh): Promise<void> {
    // Eğer mesh verilmediyse seçili mesh'i kullan
    const targetMesh = mesh || this.getSelectedMesh();
    if (!targetMesh) {
      return;
    }

    // GPU Thickness başlat (lazy init)
    if (!this.gpuThickness) {
      await this.initWebGPU();
    }

    if (!this.gpuThickness) {
      // GPU Thickness kullanilamiyor, CPU fallback
    }

    // Thickness analizi başlıyor
    this.thicknessAnalysisActive = true;

    try {
      // FikoGPUThickness modülü mevcut değil - devre dışı
      if (!this.gpuThickness) {
        return;
      }
      const thickness = this.gpuThickness;
      await thickness.init();

      // Thickness hesapla
      const result = await thickness.computeThickness(targetMesh.geometry, {
        rayOffset: 0.001,
        maxDistance: 100.0
      });

      if (!result) {
        return;
      }

      // Thickness analizi tamamlandı

      // Renk haritası oluştur
      const colorMapping = await thickness.computeColorMapping(
        result.thickness,
        result.minThickness,
        result.maxThickness
      );

      if (colorMapping) {
        // Renkleri mesh'e uygula
        thickness.applyColorsToMesh(targetMesh, colorMapping.colors);
        // Renkler uygulandı

        // Render güncelle
        this.markNeedsRender();
      }

    } catch (e) {
      // Thickness hesaplama hatasi
    } finally {
      this.thicknessAnalysisActive = false;
    }
  }

  /**
   * Seçili mesh'i al
   */
  private getSelectedMesh(): THREE.Mesh | null {
    if (!this.secililer) return null;

    // secililer'den seçili objeleri al (children property'si THREE.Group'tan geliyor)
    const secilenler = this.secililer.children;
    if (!secilenler || secilenler.length === 0) return null;

    // İlk mesh'i bul
    for (const obj of secilenler) {
      if (obj instanceof THREE.Mesh) {
        return obj;
      }
      // Eğer group ise içindeki mesh'i bul
      if (obj instanceof THREE.Group || obj instanceof THREE.Object3D) {
        const mesh = obj.children.find(c => c instanceof THREE.Mesh) as THREE.Mesh;
        if (mesh) return mesh;
      }
    }

    return null;
  }

  /**
   * Thickness analizini sıfırla (orijinal renklere dön)
   */
  resetThicknessColors(mesh?: THREE.Mesh): void {
    const targetMesh = mesh || this.getSelectedMesh();
    if (!targetMesh) return;

    // Renk attribute'unu kaldır
    if (targetMesh.geometry.hasAttribute('color')) {
      targetMesh.geometry.deleteAttribute('color');
    }

    // Material'ı güncelle
    if (targetMesh.material instanceof THREE.MeshStandardMaterial ||
        targetMesh.material instanceof THREE.MeshBasicMaterial ||
        targetMesh.material instanceof THREE.MeshPhongMaterial) {
      (targetMesh.material as any).vertexColors = false;
      targetMesh.material.needsUpdate = true;
    }

    this.markNeedsRender();
  }

  private updateLineMaterialResolution(): void {
    if (!this.sunucu || this.sahneler.length === 0) return;

    const resolution = new THREE.Vector2();
    this.sunucu.getSize(resolution);

    if (resolution.x === 0 || resolution.y === 0) {
      // Renderer size not yet available or zero, defer update
      return;
    }

    this.sahneler.forEach(sahne => {
      sahne.traverse((obj: THREE.Object3D) => {
        if (obj instanceof LineSegments2) {
          const material = obj.material;
          if (material instanceof LineMaterial) {
            // Update resolution for LineMaterial
            material.resolution.copy(resolution);
          } else if (Array.isArray(material)) {
            // Handle multi-material
            (material as THREE.Material[]).forEach(mat => {
              if (mat instanceof LineMaterial) {
                mat.resolution.copy(resolution);
              }
            });
          }
        }
      });
    });
  }

  ngOnDestroy(): void {
    // 🚀 PERFORMANS: Destroyed flag - pending RAF callback'leri iptal et
    this._isDestroyed = true;
    this.renderScheduled = false;

    document.body.style.cursor = null;

    // Timer leak fix: Clear interaction end timer
    if (this.interactionEndTimer) {
      clearTimeout(this.interactionEndTimer);
      this.interactionEndTimer = null;
    }

    // Kamera event listener'ını temizle
    if (this.aktifKamera && this.kameraChangeHandler) {
      try {
        (this.aktifKamera as any).removeEventListener?.("change", this.kameraChangeHandler);
      } catch (e) {
        // Error removing camera event listener
      }
    }

    // Event listener'ları temizle
    this.renderListeners?.forEach((value: any) => {
      if (value) value();
    });
    this.renderListeners?.clear();

    // Render'ı durdur
    this.surekliCizIptal();

    // Component'leri temizle
    this.secililer?.dispose();
    this.dc?.unsubscribe();
    this.dialogKapandiSubs?.unsubscribe();

    // Collaboration cleanup
    this.cleanupCollabEventHandlers();
    this.collabService.onRoomJoinedCallback = null;
    this.collabService.onRoomLeftCallback = null;

    // ULTRA PERFORMANS: Clean up advanced optimization systems
    if (this.raycasterBVH) {
      this.raycasterBVH.clear();
    }
    if (this.gpuCompute) {
      this.gpuCompute.dispose();
    }
    if (this.advancedLOD) {
      this.advancedLOD.dispose();
    }
    if (this.streamingLODManager) {
      this.streamingLODManager.dispose();
    }
    if (this.occlusionCulling) {
      this.occlusionCulling.dispose();
    }
    if (this.renderBatchOptimizer) {
      this.renderBatchOptimizer.dispose();
    }
    if (this.gpuMemoryManager) {
      // Memory manager cleanup (if needed)
    }
    // Worker service is singleton, so don't destroy

    // TOP 5 ULTRA PERFORMANS: Clean up latest optimization systems
    if (this.dynamicResolutionScaler) {
      this.dynamicResolutionScaler.dispose();
    }
    if (this.screenSpaceAO) {
      this.screenSpaceAO.dispose();
    }
    if (this.temporalAA) {
      this.temporalAA.dispose();
    }
    if (this.gpuParticleCompute) {
      this.gpuParticleCompute.dispose();
    }

    // NEXT 5 ULTRA PERFORMANS: Clean up second wave optimization systems
    if (this.objectPoolManager) {
      this.objectPoolManager.dispose();
    }
    // Mesh Compressor is static utility, no cleanup needed
    if (this.textureStreaming) {
      this.textureStreaming.dispose();
    }

    // EXTREME 10 MORE ULTRA PERFORMANS: Clean up third wave optimization systems
    if (this.aggressiveLOD) {
      this.aggressiveLOD.dispose();
    }
    if (this.computeWorkerPool) {
      this.computeWorkerPool.dispose();
    }
    if (this.zPrepass) {
      this.zPrepass.dispose();
    }
    if (this.frameBudget) {
      this.frameBudget.dispose();
    }
    if (this.instancedOptimizer) {
      this.instancedOptimizer.dispose();
    }
    if (this.gpuDrivenRenderer) {
      this.gpuDrivenRenderer.dispose();
    }
    if (this.visibilityPrecompute) {
      this.visibilityPrecompute.dispose();
    }
    if (this.virtualTextures) {
      this.virtualTextures.dispose();
    }
    if (this.neuralCompression) {
      this.neuralCompression.dispose();
    }

    // GPU COMPUTE EXTREME 10 MORE OPTIMIZATIONS: Clean up fourth wave systems
    if (this.gpuPhysicsCompute) {
      this.gpuPhysicsCompute.dispose();
    }
    if (this.gpuFrustumCulling) {
      this.gpuFrustumCulling.dispose();
    }
    if (this.gpuOcclusionQueries) {
      this.gpuOcclusionQueries.dispose();
    }
    if (this.gpuTransformFeedback) {
      this.gpuTransformFeedback.dispose();
    }
    if (this.gpuSkinning) {
      this.gpuSkinning.dispose();
    }
    if (this.gpuParticleAdvection) {
      this.gpuParticleAdvection.dispose();
    }
    if (this.gpuTextureFiltering) {
      this.gpuTextureFiltering.dispose();
    }
    if (this.gpuTileDeferredRendering) {
      this.gpuTileDeferredRendering.dispose();
    }
    if (this.gpuAsyncComputePipeline) {
      this.gpuAsyncComputePipeline.dispose();
    }
    if (this.gpuStreamOutputList) {
      this.gpuStreamOutputList.dispose();
    }
    if (this.gpuComputeOptimized) {
      this.gpuComputeOptimized.dispose();
    }

    // 🚀 WebGPU Camera Ops cleanup
    if (this.webgpuCameraOps) {
      this.webgpuCameraOps.dispose();
      this.webgpuCameraOps = null;
    }
    this.cachedObjectBounds = [];
    this.objectBoundsCacheValid = false;
    this.lastCameraOpsResult = null;

    // 🚀 WebGPU Lighting cleanup
    if (this.webgpuLighting) {
      this.webgpuLighting.dispose();
      this.webgpuLighting = null;
    }
    this.cachedFikoIsikYonsels = [];
    this.lightCacheValid = false;

    // 🚀🚀🚀 CATIA-LEVEL Performance Integration cleanup (FAZA 4)
    if (this.performanceIntegration) {
      this.performanceIntegration.dispose();
      this.performanceIntegration = null;
    }
    // Also dispose global singleton
    disposePerformanceIntegration();

    // Note: FikoDeferredRenderer is disabled by default due to WebGL feedback loop issues
    // It can be instantiated and enabled later if needed for multi-light scenarios

    // Optimizasyon sistemlerini temizle
    this.lodManager.dispose();
    this.sceneOptimizer.dispose();
    this.postProcessing.dispose();
    this.meshPool.clear();
    this.geometryPool.clear();

    // Önbellekleri temizle
    this.textureCache.forEach(tex => tex.dispose());
    this.materialCache.forEach(mat => mat.dispose());
    this.geometryCache.forEach(geo => geo.dispose());
    this.textureCache.clear();
    this.materialCache.clear();
    this.geometryCache.clear();

    // Kontrol event listener'larini temizle (start/change/end)
    if (this.aktifKontrol) {
      this._removeControlListeners();
      this.aktifKontrol.dispose();
    }

    // WebGL context listener'larını temizle
    if (this.sunucu?.domElement) {
      if (this._onContextLost) this.sunucu.domElement.removeEventListener('webglcontextlost', this._onContextLost);
      if (this._onContextRestored) this.sunucu.domElement.removeEventListener('webglcontextrestored', this._onContextRestored);
    }
    this._onContextLost = null;
    this._onContextRestored = null;

    // Renderer'ları temizle
    this.sunucu?.renderLists?.dispose();
    this.sunucu?.dispose();
    this.sunucuCSS3d = null;

    if (this.sunucuCSS2d) {
      this.sunucuCSS2d.domElement?.remove();
      this.sunucuCSS2d = null;
    }

    this._css3dStyleEl?.remove();
    this._css3dStyleEl = null;

  }

  trackByMenuBaslik(index: number, item: any): string {
    return item?.baslik ?? index;
  }
}